import { createContext, useContext, useMemo, type ReactNode } from "react";

export type Lang = "en" | "es";

/**
 * A deliberately small translation layer for the account screens (sign-up, login, team, account, onboarding).
 * The agent builder screens are still English-only; moving them here is a later job (docs/IDENTITY.md).
 * Keys are the English words in `en`; a missing Spanish entry falls back to English, so a gap never shows a key.
 */
const en = {
  "brand": "Nemo",
  "lang.en": "English",
  "lang.es": "Español",
  "lang.label": "Language",
  "common.email": "Email",
  "common.password": "Password",
  "common.name": "Your name",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saved": "Saved.",
  "common.loading": "Loading…",
  "common.remove": "Remove",
  "common.back": "Back",
  "common.copy": "Copy",
  "common.or": "or",
  "common.network": "We could not reach the server. Check your connection and try again.",
  "common.generic": "Something went wrong on our side. Try again in a moment.",
  "login.title": "Sign in to Nemo",
  "login.submit": "Sign in",
  "login.busy": "Signing in…",
  "login.forgot": "Forgot your password?",
  "login.noAccount": "New to Nemo?",
  "login.create": "Create an account",
  "login.google": "Continue with Google",
  "login.apiKey": "Sign in with an API key instead",
  "login.googleFailed": "We could not sign you in with Google. Please try again, or use your email and password.",
  "login.notStaff": "That Google account does not have access to the admin area.",
  "login.sessionEnded": "Your session ended. Sign in again; your latest changes were saved.",
  "login.verifyFirst": "Please confirm your email first. We sent you a link.",
  "login.resend": "Send the link again",
  "login.resent": "If that address needs confirming, we sent a new link.",
  "signup.title": "Create your Nemo account",
  "signup.lead": "Build a chat agent for your website in a few minutes. No credit card needed to start.",
  "signup.business": "Business or website name",
  "signup.businessHelp": "Optional. You can change it later.",
  "signup.passwordHelp": "At least 10 characters. A short phrase works well.",
  "signup.submit": "Create account",
  "signup.busy": "Creating…",
  "signup.haveAccount": "Already have an account?",
  "signup.terms": "By creating an account you agree to the terms and the privacy policy on our website.",
  "check.title": "Check your email",
  "check.body": "We sent a link to {email}. Open it to confirm your address and start building. The link works once and expires in 24 hours.",
  "check.spam": "Nothing there? Look in your spam folder, or",
  "check.again": "send the link again",
  "forgot.title": "Reset your password",
  "forgot.lead": "Enter your email and we will send you a link to choose a new password.",
  "forgot.submit": "Send me the link",
  "forgot.done": "If that address has an account, we sent a link. It works once and expires in 1 hour.",
  "reset.title": "Choose a new password",
  "reset.submit": "Save new password",
  "reset.done": "Your password was changed. You were signed out everywhere; sign in with the new one.",
  "verify.working": "Confirming your email…",
  "verify.failed": "This link is no longer valid. Sign in and we will send you a new one.",
  "invite.title": "You have been invited",
  "invite.body": "Join “{account}” as {role}.",
  "invite.newPerson": "Create your login to join. Your email is {email}.",
  "invite.signInFirst": "{email} already has a Nemo login. Sign in with it to accept the invitation.",
  "invite.accept": "Accept invitation",
  "invite.invalid": "This invitation is no longer valid. Ask for a new one.",
  "role.owner": "Owner",
  "role.manager": "Manager",
  "role.member": "Member",
  "role.owner.help": "Everything, including the account itself and its billing.",
  "role.manager.help": "Publish agents, manage API keys and the team (except owners).",
  "role.member.help": "Build and test agents and see leads. Cannot publish or manage the team.",
  "nav.agents": "Agents",
  "nav.leads": "Leads",
  "nav.notifications": "Notifications",
  "nav.team": "Team",
  "nav.account": "Account",
  "nav.keys": "API keys",
  "nav.signout": "Sign out",
  "switcher.label": "Account",
  "switcher.new": "Create another account…",
  "switcher.newName": "Name of the new account",
  "onboarding.title": "Welcome to Nemo, {name}",
  "onboarding.lead": "Let's make your first agent. It answers your visitors' questions from what you teach it, on your website, any time of day.",
  "onboarding.step1": "1. Name it and say what it does",
  "onboarding.step2": "2. Teach it (your website, files, questions)",
  "onboarding.step3": "3. Try it and publish it",
  "onboarding.agentName": "Name of your agent",
  "onboarding.agentNameHelp": "Up to 20 characters. Visitors will see it.",
  "onboarding.job": "What should it help with?",
  "onboarding.jobHelp": "For example: answer questions about our prices and opening hours.",
  "onboarding.start": "Create my agent",
  "onboarding.aura": "Prefer to be guided? Aura, our assistant that builds the agent with you, is coming soon.",
  "onboarding.unverified": "Please confirm your email (we sent you a link) before you publish.",
  "team.title": "Team",
  "team.lead": "People who can work in this account. Everyone signs in with their own email.",
  "team.you": "(you)",
  "team.invite": "Invite someone",
  "team.inviteEmail": "Their email",
  "team.inviteRole": "Role",
  "team.send": "Send invitation",
  "team.sending": "Sending…",
  "team.sent": "Invitation sent to {email}.",
  "team.pending": "Waiting to join",
  "team.pendingNone": "No invitations waiting.",
  "team.expires": "expires {date}",
  "team.revoke": "Cancel invitation",
  "team.changeRole": "Role of {name}",
  "team.remove": "Remove",
  "team.removeConfirm": "Remove {name} from this account? They lose access right away.",
  "team.leave": "Leave this account",
  "team.leaveConfirm": "Leave “{account}”? You will lose access to its agents.",
  "team.lastOwner": "An account needs at least one owner.",
  "team.cannotInvite": "Only owners and managers can invite people.",
  "team.members": "Members",
  "team.verifyFirst": "Confirm your own email before inviting people.",
  "account.title": "Account",
  "account.you": "You",
  "account.profile": "Your profile",
  "account.language": "Language",
  "account.languageHelp": "Used for the screens in this section and for the emails we send you.",
  "account.password": "Change password",
  "account.currentPassword": "Current password",
  "account.newPassword": "New password",
  "account.passwordChanged": "Password changed. Your other devices were signed out.",
  "account.noPassword": "You sign in with Google, so there is no password to change.",
  "account.sessions": "Where you are signed in",
  "account.sessionsLead": "If you see a device you do not know, end that session and change your password.",
  "account.thisDevice": "This device",
  "account.endSession": "End session",
  "account.signOutAll": "Sign out everywhere",
  "account.workspace": "This account",
  "account.workspaceName": "Account name",
  "account.onlyOwner": "Only an owner can change these settings.",
  "account.plan": "Plan",
  "account.delete": "Delete this account",
  "account.deleteWarn": "This hides the account and everything in it right away: agents stop answering, API keys stop working and nobody can sign in to it. We keep the data for {days} days in case you change your mind (write to us and we can restore it); after that it is erased for good. We e-mail the owners a link to get a copy of your data (agents, knowledge, conversations and leads), and send two reminders. The download link works for 15 days.",
  "account.deleteConfirm": "To confirm, type the account name exactly: {name}",
  "account.deleteButton": "Delete the account",
  "account.deleted": "The account was deleted.",
  "upgrade.cta": "See plans",
  "upgrade.title": "Your plan has reached its limit",
  "keys.note": "API keys are only for connecting your own systems. You do not need one to use Nemo here.",
  "keys.forbidden": "Only owners and managers can manage API keys.",
  "mfa.challengeTitle": "Enter your code",
  "mfa.challengeLead": "Open your authenticator app and type the 6-digit code for Nemo.",
  "mfa.code": "6-digit code",
  "mfa.verify": "Continue",
  "mfa.useRecovery": "I lost my phone: use a recovery code",
  "mfa.useApp": "Use the code from my app instead",
  "mfa.recoveryCode": "Recovery code",
  "mfa.recoveryLeft": "Recovery codes left: {n}. Make a new set in Account, Security.",
  "mfa.setupTitle": "Set up two-step verification",
  "mfa.setupLeadRequired": "To protect your account, two-step verification is required. It takes two minutes.",
  "mfa.setupLead": "Two-step verification asks for a code from your phone each time you sign in, so a stolen password is not enough.",
  "mfa.step1": "1. Install an authenticator app (Google Authenticator, Microsoft Authenticator, 1Password, Authy…).",
  "mfa.step2": "2. In the app tap “Add account” (or “+”), choose “Scan QR code” and point your camera at this code:",
  "mfa.qrAlt": "QR code that adds Nemo to your authenticator app",
  "mfa.noScan": "Can’t scan the code? Enter a key by hand instead",
  "mfa.manual": "In the app choose “Enter a setup key” and type this key:",
  "mfa.openApp": "On this phone? Open it directly in your app",
  "mfa.step3": "3. Type the 6-digit code the app shows now:",
  "mfa.start": "Start",
  "mfa.savedTitle": "Save your recovery codes",
  "mfa.savedLead": "If you lose your phone, each of these codes lets you in once. Keep them somewhere safe (a password manager or a printed page). We cannot show them again.",
  "mfa.savedCheck": "I saved my recovery codes",
  "mfa.done": "Done",
  "mfa.security": "Two-step verification",
  "mfa.on": "On. You will be asked for a code each time you sign in.",
  "mfa.off": "Off. Turn it on to protect your account with your phone.",
  "mfa.turnOn": "Turn on",
  "mfa.turnOff": "Turn off",
  "mfa.turnOffLead": "Type a current code from your app to turn it off.",
  "mfa.requiredNote": "Required for your account; it cannot be turned off.",
  "mfa.newCodes": "Make new recovery codes",
  "mfa.newCodesNote": "The old codes stop working.",
  "mfa.signOut": "Sign out",
  "mfa.nudgeFirst": "Protect your account: turn on two-step verification. It asks for a code from your phone when you sign in, so a stolen password is not enough. It takes two minutes.",
  "mfa.nudgeTrial": "Your free trial has ended. Now that your agents are working for your business, we recommend turning on two-step verification.",
  "mfa.nudgeSetup": "Set up now",
  "mfa.nudgeLater": "Not now",
  "export.title": "Get a copy of your data",
  "export.lead": "The account “{account}” was deleted. We keep its data until {date}; after that it is erased for good.",
  "export.what": "You can ask for a copy now: one file with your agents, knowledge and conversations, and a spreadsheet with your leads. We e-mail the owners a download link when it is ready. The link works for 15 days.",
  "export.button": "Send me my data",
  "export.sending": "Preparing…",
  "export.sent": "Done. We sent the download link to the owners of the account. Check your inbox (and spam).",
  "export.invalid": "This link is no longer valid. The data may already have been erased.",
  "export.tooMany": "We already sent the link a few times. Check your inbox and spam folder, or try again in an hour.",
  "download.title": "Download your data",
  "download.lead": "Two files: everything in one file, and your leads as a spreadsheet. Keep them somewhere safe: they contain your customers' details.",
  "download.json": "Download everything (JSON)",
  "download.csv": "Download leads (spreadsheet)",
  "download.invalid": "This download link has expired or is not valid.",
  "signup.inviteOnlyTitle": "Nemo is invitation-only right now",
  "signup.inviteOnlyBody": "We are letting people in by invitation while we get ready. If you have an invitation, open the link we e-mailed you. If you do not have one, write to us and we will add you.",
  "signup.domainsLead": "Nemo is invitation-only right now. Addresses ending in {domains} can create an account (confirm your e-mail, then set up two-step verification); everyone else needs an invitation.",
  "signup.closedTitle": "Nemo is not accepting new accounts right now",
  "signup.closedBody": "New accounts are paused. If you already have an account, you can still sign in.",
  "signup.invalidInvite": "This invitation is no longer valid",
  "signup.invitedLead": "You are invited. Create your account with {email}.",
  "signup.invitedEmailHelp": "This is the address your invitation was sent to.",
  "login.signupClosed": "Nemo is invitation-only right now: a new account needs an invitation. If you already have an account, sign in with the same Google address you used before.",
  "act.link": "History",
  "act.title": "History of {name}",
  "act.back": "Back to the agent",
  "act.tab.activity": "Activity",
  "act.tab.versions": "Versions",
  "act.lead": "Who created, changed and published this agent, and when. Only owners and managers can see this.",
  "act.forbidden": "Only owners and managers can see the history of an agent.",
  "act.empty": "Nothing recorded yet.",
  "act.noMatch": "Nothing matches these filters.",
  "act.filter.action": "What happened",
  "act.filter.any": "Anything",
  "act.filter.person": "Who",
  "act.filter.from": "From",
  "act.filter.to": "To",
  "act.more": "Show older entries",
  "act.who.system": "Nemo (automatic)",
  "act.who.partner": "A partner connection",
  "act.who.key": "API key “{name}”",
  "act.via": "from {client}",
  "act.action.created": "{who} created this agent",
  "act.action.edited": "{who} changed {what}",
  "act.action.published": "{who} published version {n}",
  "act.action.publishedFirst": "{who} published version {n} (the first one)",
  "act.action.unpublished": "{who} took the agent offline (unpublished)",
  "act.action.reverted": "{who} put the settings of version {n} back into the draft",
  "act.action.deleted": "{who} deleted this agent",
  "act.action.source_added": "{who} added knowledge: {what}",
  "act.action.source_removed": "{who} removed knowledge: {what}",
  "act.action.transferred": "{who} moved this agent",
  "act.action.shared": "{who} shared this agent",
  "act.action.unshared": "{who} stopped sharing this agent",
  "act.andMore": "{list} and {n} more",
  "act.changedSet": "set",
  "act.changedCleared": "cleared",
  "act.chars": "{n} characters",
  "act.items": "{n} items",
  "act.fromTo": "{from} → {to}",
  "act.changedOnly": "changed",
  "act.src.text": "text",
  "act.src.file": "a file",
  "act.src.url": "a web page",
  "act.src.crawl": "a website",
  "act.src.other": "a source",
  "act.f.name": "the name",
  "act.f.creatorName": "the creator name",
  "act.f.avatarUrl": "the avatar",
  "act.f.persona": "the job and personality",
  "act.f.responseInstructions": "the response instructions",
  "act.f.greetings": "the greetings",
  "act.f.conversationStarters": "the conversation starters",
  "act.f.strictness": "the strictness",
  "act.f.creativity": "the creativity",
  "act.f.model.generative.model": "the writing model",
  "act.f.model.generative.provider": "the model provider",
  "act.f.knowledge.faqs": "the questions and answers",
  "act.f.knowledge.topics": "the topics",
  "act.f.leadCapture.enabled": "collecting contact details",
  "act.f.leadCapture.fields": "the contact details asked for",
  "act.f.leadCapture.notifyEmails": "who is notified about leads",
  "act.f.leadCapture.crm": "the lead destination",
  "act.f.leadCapture.flow": "how the agent asks for details",
  "act.f.deployment.website.theme": "the colour",
  "act.f.deployment.website.mode": "how the chat appears on the page",
  "act.f.deployment.website.position": "the position",
  "act.f.deployment.website.style": "the style",
  "act.f.deployment.website.language": "the chat language",
  "act.f.deployment.website.allowedDomains": "the websites allowed",
  "act.f.deployment.website.brandName": "the brand name",
  "act.f.deployment.website.voiceInput": "voice input",
  "act.f.deployment.website.attribution.enabled": "source tracking",
  "act.f.deployment.website.publicChat": "the public chat link",
  "act.f.legalNotice": "the legal notice",
  "act.f.allowModelTraining": "allowing model training",
  "act.f.other": "other settings",
  "act.v.lead": "Each time you publish, that version is kept. You can put the settings of an earlier one back into the draft to review, then publish again if you want.",
  "act.v.empty": "This agent has not been published yet.",
  "act.v.version": "Version",
  "act.v.when": "Published",
  "act.v.by": "By",
  "act.v.live": "Live now",
  "act.v.revert": "Revert to this version",
  "act.v.revertAria": "Revert to version {n}",
  "act.v.confirm": "Put the settings of version {n} back into the draft? Nothing is published and what is live does not change. Your current draft is replaced (it stays in the history). Files and websites you added or removed since are not part of a version and stay as they are.",
  "act.v.confirmYes": "Yes, put them in the draft",
  "act.v.done": "Done. The settings of version {n} are in the draft. Review them, then publish when you are happy.",
  "act.v.review": "Review the draft",
  "act.v.failed": "Could not put that version back: {why}",
  "act.a.created": "Created",
  "act.a.edited": "Settings changed",
  "act.a.published": "Published",
  "act.a.unpublished": "Unpublished",
  "act.a.reverted": "Put back from a version",
  "act.a.deleted": "Deleted",
  "act.a.source_added": "Knowledge added",
  "act.a.source_removed": "Knowledge removed",
  "act.a.transferred": "Moved",
  "act.a.shared": "Shared",
  "act.a.unshared": "Sharing stopped",
  "err.invalid_credentials": "That email and password do not match. Check them and try again.",
  "err.forbidden": "Your role does not allow this. Ask an account owner or manager.",
  "err.not_a_member": "You do not belong to that account.",
  "err.account_suspended": "This account is paused. Contact support.",
  "err.account_deleted": "This account was deleted.",
  "share.title": "Sharing",
  "share.back": "← Back to the agent",
  "share.badge": "Shared",
  "share.sharedBy": "Shared with you by {account}. Your role: {role}.",
  "share.ownedBy": "This agent belongs to “{account}”.",
  "share.people": "People with access",
  "share.accountTeam": "Plus the {n} people on the account team.",
  "share.noGuests": "Nobody else has access yet.",
  "share.invite": "Share with someone",
  "share.inviteHelp": "They get access to this agent only, not to the rest of your account. They can have their own login, even in another company.",
  "share.email": "Their email",
  "share.role": "Role",
  "share.send": "Send invitation",
  "share.sending": "Sending…",
  "share.sent": "Invitation sent to {email}.",
  "share.pending": "Invitations waiting",
  "share.pendingNone": "No invitations waiting.",
  "share.revoke": "Cancel invitation",
  "share.remove": "Remove",
  "share.removeConfirm": "Remove {name}? They lose access right away.",
  "share.leave": "Leave this agent",
  "share.leaveConfirm": "Leave “{agent}”? You will no longer see it.",
  "share.changeRole": "Role of {name}",
  "share.expires": "expires {date}",
  "role.viewer": "Viewer",
  "role.editor": "Editor",
  "role.viewer.help": "Can look at the agent and try it. Cannot change it.",
  "role.editor.help": "Can change the agent and add knowledge. Cannot publish.",
  "role.manager.agentHelp": "Can also publish, and share with viewers and editors.",
  "share.leadsNote": "People you share an agent with never see its leads or where they are sent.",
  "transfer.peopleTitle": "Who has access to this agent",
  "transfer.peopleNone": "Nobody else has access.",
  "transfer.peopleHelp": "They kept the access they had. Remove anyone you do not know.",
  "transfer.manage": "Manage access",
  "plan.upgrade": "See plans",
  "transfer.overPlan": "Your plan has no room for this agent yet, so it is view-only: you can look at it and try it, but not change it or publish it until you upgrade or free up room.",
  "share.readOnly": "You can look at this agent and try it, but not change it.",
  "share.transfer": "Transfer ownership",
  "share.transferHelp": "Give this agent to someone else. They choose which of their accounts receives it. Nothing changes until they accept.",
  "share.transferMoves": "Moves with it: the agent and its versions, knowledge sources, conversations, leads and their delivery history. The website chat keeps working.",
  "share.transferResets": "Changes: new leads are sent to the new owner's email instead of yours, and any CRM connection is removed. People you shared the agent with keep their access until the new owner removes it.",
  "share.transferEmail": "Email of the new owner",
  "share.transferConfirm": "Type the agent's name to confirm: {name}",
  "share.transferKeep": "Keep my access afterwards",
  "share.keep.none": "No access",
  "share.transferSend": "Send transfer offer",
  "share.transferSent": "Offer sent to {email}. It expires on {date}.",
  "share.transferOpen": "Offer waiting for {email} (expires {date}).",
  "share.transferCancel": "Cancel the offer",
  "share.transferOnlyOwner": "Only an owner or manager of the account can transfer this agent.",
  "shared.section": "Shared with you",
  "shared.owner": "from {account}",
  "shared.open": "Open",
  "agents.share": "Share",
  "transfer.title": "Someone wants to give you an agent",
  "transfer.body": "{sender} ({account}) wants to transfer “{agent}” to you, with its knowledge, conversations and leads.",
  "transfer.choose": "Which of your accounts should receive it?",
  "transfer.accept": "Accept and move it here",
  "transfer.decline": "Decline",
  "transfer.accepted": "Done. “{agent}” is now in your account. New leads now go to your email.",
  "transfer.declined": "You declined the offer.",
  "transfer.invalid": "This offer is no longer valid.",
  "transfer.signInFirst": "Sign in with {email} to see this offer.",
  "transfer.noLogin": "Create an account with {email} (and confirm it), then open this link again.",
  "transfer.noAccounts": "You need to be an owner or manager of an account to receive an agent.",
  "transfer.pendingBanner": "{n} agent offer(s) waiting for you.",
  "transfer.review": "Review",
  "shareAccept.title": "An agent was shared with you",
  "shareAccept.body": "Open “{agent}” from “{account}” as {role}.",
  "shareAccept.newPerson": "Create your login to open it. Your email is {email}.",
  "shareAccept.signInFirst": "{email} already has a Nemo login. Sign in with it to accept.",
  "shareAccept.accept": "Accept",
  "shareAccept.invalid": "This invitation is no longer valid. Ask for a new one.",
  "nav.billing": "Billing",
  "billing.title": "Billing",
  "billing.lead": "Your plan, what you have used this month, and your invoices. Payments are handled securely by Stripe: Nemo never sees your card number.",
  "billing.ownerOnly": "Only the account owner can see billing.",
  "billing.unavailable": "Billing is not switched on yet.",
  "billing.current": "Your plan",
  "billing.noPlan": "You do not have a plan yet. You can keep building; choose a plan to put your agent on your website.",
  "billing.state.trialing": "Free trial",
  "billing.state.active": "Active",
  "billing.state.grace": "Payment problem",
  "billing.state.suspended": "Paused",
  "billing.state.ended": "Ended",
  "billing.state.none": "No plan",
  "billing.trialEnds": "Your free trial ends on {date}. After that we charge {amount} to your card, unless you cancel first.",
  "billing.renews": "Renews on {date}.",
  "billing.cancels": "Your plan ends on {date}. You keep everything until then.",
  "billing.grantedByStaff": "This plan was given to your account by the Nemo team.",
  "billing.manage": "Manage payment details and invoices",
  "billing.change": "Change plan",
  "billing.opening": "Opening…",
  "billing.usage": "This month",
  "billing.usageConversations": "Conversations",
  "billing.usageAgents": "Agents",
  "billing.usageTeam": "Teammates",
  "billing.usedOf": "{used} of {limit} used",
  "billing.usedNoLimit": "{used} used, no limit",
  "billing.nearly": "You are close to the limit. Upgrade so visitors can keep chatting.",
  "billing.full": "The limit is reached. Visitors see that the assistant is very busy until the next period, or until you upgrade.",
  "billing.plans": "Plans",
  "billing.monthly": "Monthly",
  "billing.yearly": "Yearly",
  "billing.perMonth": "{price} / month",
  "billing.billedYearly": "{total} billed yearly",
  "billing.billedMonthly": "Billed monthly",
  "billing.agent1": "1 agent",
  "billing.agentN": "{n} agents",
  "billing.startTrial": "Start your {days}-day free trial",
  "billing.subscribe": "Subscribe",
  "billing.yourPlan": "Your plan",
  "billing.chosen": "Chosen for you",
  "billing.trialNote": "A card is needed to start. We charge it when the trial ends unless you cancel, and we remind you 3, 2 and 1 days before and on the day. No refunds: each case is reviewed individually.",
  "billing.noTrialNote": "You are charged today. No refunds: each case is reviewed individually.",
  "billing.feature.analytics": "Agent analytics",
  "billing.feature.remove_branding": "Your own branding (no Nemo logo)",
  "billing.feature.advanced_widget": "Advanced widget customization",
  "billing.feature.lead_generation": "Lead generation module",
  "billing.feature.cloud_integrations": "Cloud integrations (Google Cloud, AWS)",
  "billing.feature.bring_your_llm": "Bring your own AI model",
  "billing.feature.automation_connections": "Automation platform connections",
  "billing.feature.dedicated_support": "Dedicated customer support",
  "billing.invoices": "Invoices",
  "billing.noInvoices": "No invoices yet.",
  "billing.invoiceView": "View",
  "billing.inv.paid": "Paid",
  "billing.inv.open": "Due",
  "billing.inv.draft": "Draft",
  "billing.inv.void": "Void",
  "billing.inv.uncollectible": "Not collected",
  "billing.processing": "Thank you! We are confirming your payment with Stripe. This takes a few seconds and this page updates by itself.",
  "billing.confirmed": "Your plan is active. Thank you!",
  "billing.checkoutCancelled": "Checkout was cancelled. Nothing was charged.",
  "billing.held": "{n} of your agents are paused because your plan includes fewer agents. Nothing was deleted.",
  "billing.banner.grace": "We could not take your last payment. Everything keeps working until {date}. Please update your card.",
  "billing.banner.suspended": "Your payment did not go through, so your published agents are paused. Update your card and they come back right away.",
  "billing.banner.ended": "Your subscription has ended and your published agents are paused. Your data is kept. Choose a plan to bring them back.",
  "billing.banner.trial": "Your free trial ends on {date}. We charge your card then, unless you cancel first.",
  "billing.banner.cta": "Open billing",
  "embed.title": "Add it to your website",
  "embed.helpPublished": "Copy this one line and paste it into your website. You only do it once.",
  "embed.helpDraft": "Publish first, then paste this into your website. It will not load until the agent is published.",
  "embed.code": "Embed code",
  "embed.copy": "Copy code",
  "embed.copied": "Copied",
  "embed.never": "This line never changes when you edit or republish the agent. Appearance, texts and knowledge are loaded from Nemo each time.",
  "embed.where": "Where do I paste it?",
  "embed.wordpress": "WordPress",
  "embed.wordpress.how": "Install the free plugin \"WPCode\", open Code Snippets > Header & Footer, paste the line in the Footer box and save.",
  "embed.wix": "Wix",
  "embed.wix.how": "Settings > Custom Code > Add Code. Paste the line, choose \"Body - end\" and \"All pages\", then publish your site. (Do not use the \"Embed HTML\" element for the bubble: it cannot leave its frame.) To show the chat inside a page instead, turn on \"Public chat link\" below and use Add > Embed Code > Embed a site with that link, after adding your Wix address to Allowed domains.",
  "embed.squarespace": "Squarespace",
  "embed.squarespace.how": "Settings > Developer Tools > Code Injection. Paste the line in the Footer box and save.",
  "embed.shopify": "Shopify",
  "embed.shopify.how": "Online Store > Themes > Edit code > theme.liquid. Paste the line just before </body> and save.",
  "embed.html": "Plain HTML",
  "embed.html.how": "Paste the line just before the closing </body> tag of each page.",
  "embed.gtm": "Google Tag Manager",
  "embed.gtm.how": "Tags > New > Custom HTML. Paste the line, trigger it on All Pages and publish the container.",
  "embed.inline": "Want the chat inside a page instead of a bubble?",
  "embed.inline.how": "Put this box on the page where the chat should appear. If the page has the box, the chat is shown inside it; if not, it is a floating bubble.",
  "embed.inline.label": "Box for the chat",
  "link.title": "Public chat link",
  "link.toggle": "Anyone with the link can chat with this agent",
  "link.help": "Gives the agent its own web page that you can share, put behind a QR code, or show inside a website builder that only accepts a page address (for example Wix \"Embed a site\"). Off by default. Visitors can chat with no login, so it counts towards your conversations like any other chat. It does not add any domain to your allowed domains.",
  "link.publish": "Publish the agent to make the link work. Changes to this switch apply when you publish.",
  "link.label": "Public chat link",
  "link.copy": "Copy link",
  "link.frames": "To show this page inside another website, add that website to Allowed domains. Other websites cannot frame it.",
  "blocked.title": "Websites that tried to load this agent",
  "blocked.help": "These websites have the Nemo script but are not in your allowed domains, so the chat did not appear there. Nothing is added unless you press Allow: the allowed list is what stops other people from running up your AI bill.",
  "blocked.tried": "{origin} tried to load this agent. Allow it?",
  "blocked.triedNote": "{origin} tried to load \"{agent}\". Allow it?",
  "blocked.adds": "Adds {entries}.",
  "mm.title": "Action needed: your agent's search needs a new version",
  "mm.body": "The service that powers this agent's search is being retired. We have prepared a new version of its knowledge in the background; visitors are not affected yet. Please test it and, if you changed your documents recently, re-run the training before you switch.",
  "mm.preparing": "We are still preparing the new version ({done} of {total} passages). We will tell you when it is ready to test.",
  "mm.preparingNoCount": "We are still preparing the new version. We will tell you when it is ready to test.",
  "mm.days": "If nobody switches, we will switch it automatically in {n} days.",
  "mm.daysOne": "If nobody switches, we will switch it automatically tomorrow.",
  "mm.daysToday": "If nobody switches, we will switch it automatically today.",
  "mm.pastDue": "The switch date has passed.",
  "mm.testTitle": "Test with the new version",
  "mm.testHint": "Ask the agent a question it should know. The answer comes from the new version; nothing is saved or changed.",
  "mm.testLabel": "Your test question",
  "mm.testButton": "Test with the new version",
  "mm.testing": "Testing…",
  "mm.answer": "Answer from the new version:",
  "mm.tested": "You have tested the new version.",
  "mm.switch": "Switch now",
  "mm.switching": "Switching…",
  "mm.reviewed": "I have reviewed it",
  "mm.notReady": "Switching and testing are available once the new version is ready.",
  "mm.ackedTitle": "Search change reviewed",
  "mm.ackedBody": "You marked this as reviewed. You can still test and switch whenever you are ready.",
  "mm.switchedTitle": "Switched to the new version",
  "mm.switchedBody": "Your agent now answers from the new version. Re-run the training if you changed documents recently.",
  "mm.undo": "You can undo this until {date}.",
  "mm.open": "Open the agent",
  "src.label": "Remember where visitors came from",
  "src.help": "During a visit, the chat remembers the page and campaign link the visitor arrived from (for example a Google Ads link) and sends it with their messages, so you can see which channel brought each conversation or lead. The full page address is kept, including its parameters (values that look like passwords or tokens are hidden), together with the page that referred them. In the visitor's browser it is held only in the open tab. On by default.",
  "src.notice": "Add this line to your privacy notice: “Our chat remembers, during your visit, the page and campaign link you arrived from, to tell us which of our channels brought you.”",
  "mm.current": "Current search model",
  "mm.new": "New search model",
  "mm.retires": "The current one is retired on",
  "mm.switchesOn": "Automatic switch on",
  "mm.notice": "Read the provider's official notice",
  "mm.newTab": "(opens in a new tab)",
  "mm.noNotice": "No provider notice available for this change.",
  "blocked.allow": "Allow",
  "blocked.dismiss": "Dismiss",
  "blocked.allowLabel": "Allow {origin}",
  "blocked.dismissLabel": "Dismiss {origin}",
  "blocked.allowed": "Allowed: {entries}. The chat works there now.",
  "blocked.dismissed": "Dismissed.",
  "blocked.none": "No website has tried to load this agent without being allowed.",
  "blocked.suggest": "Is {domain} your company's website? You can allow it.",
  "blocked.suggestAllow": "Allow {domain}",
  "blocked.ask": "Ask an owner or manager to allow it.",
  "blocked.failed": "Could not do that. Try again.",
  "checksite.title": "Check my website",
  "check.help": "Enter the address of a page where you pasted the line. We read the page's text to see if it is set up right. We never run anything from your page.",
  "check.label": "Page address",
  "check.button": "Check",
  "check.busy": "Checking…",
  "check.good": "Everything looks right.",
  "check.fix": "Fix the items marked above, then check again.",
  "check.p.invalid_url": "That does not look like a web page address. Try something like www.example.com.",
  "check.p.blocked": "We can only read public websites. That address is not allowed.",
  "check.p.timeout": "The page took too long to answer.",
  "check.p.unreachable": "We could not reach that page. Check the address and that the site is online.",
  "check.p.http_error": "The page answered with an error ({status}). Open it in your browser to check it exists and is public.",
  "check.p.not_html": "That address is not a web page.",
  "check.p.too_large": "That page is too large to check.",
  "check.p.rate_limited": "Please wait a moment before checking again.",
  "check.agent.published": "The agent is published.",
  "check.agent.not_published": "The agent is not published yet. Publish it below, then check again.",
  "check.script.ok": "The Nemo line is on the page and points to this agent.",
  "check.script.ok_with_api": "The Nemo line is on the page. It is the older version with an API address; it still works, but you can replace it with the shorter line above.",
  "check.script.missing": "We could not find the Nemo line on this page. Paste it as shown above. If you add it through Google Tag Manager or a builder that adds code after the page loads, this check cannot see it.",
  "check.script.wrong_agent": "The page has a Nemo line for a different agent ({found}). Replace it with the line above.",
  "check.script.wrong_address": "The Nemo line is there, but its address ({found}) is not the current one. Replace it with the line above; the address should be {expected}.",
  "check.script.twice": "The line appears more than once. Keep just one.",
  "check.script.commented_out": "The Nemo line is on the page but inside a comment, so it does not run. Remove the comment marks around it.",
  "check.script.consent_blocked": "The Nemo line is on the page, but your cookie tool holds it back until visitors accept cookies. Until then nobody will see the chat. In your cookie tool, mark the line as necessary (or allow it), or accept that only visitors who agree will see it.",
  "check.csp.script_blocked": "Your site's security settings (Content-Security-Policy) do not allow the Nemo script. Ask whoever manages the site to add {host} to script-src.",
  "check.csp.connect_blocked": "Your site's security settings (Content-Security-Policy) do not let the chat talk to Nemo. Ask whoever manages the site to add {host} to connect-src.",
  "check.csp.present_ok": "Your site has security settings (Content-Security-Policy) and they allow the chat.",
  "check.allowed.allowed": "{site} is on the allowed list.",
  "check.allowed.not_allowed": "{site} is not on the allowed list, so the chat will not appear there.",
  "check.allowed.add": "Add {entries} to allowed domains",
  "check.allowed.added": "Added. Publish to make it live.",
  "drive.title": "Google Drive",
  "drive.help": "Choose files from your Google Drive for the agent to learn from. Nemo can only see the files you choose.",
  "drive.privacy": "What you connect is stored and used to answer anyone who chats with this agent, so do not connect confidential files. Nemo only opens the files you pick, and you can disconnect at any time.",
  "drive.connect": "Connect Google Drive",
  "drive.connectAgain": "Connect again",
  "drive.opening": "Opening…",
  "drive.status.active": "Connected",
  "drive.status.needsReconnect": "Needs attention",
  "drive.connectedAs": "Google account: {email}",
  "drive.needsReconnect": "Access to Google Drive was removed or expired. The agent keeps what it already read, but it is no longer updated. Connect again to continue.",
  "drive.keepSynced": "Keep these files up to date every day",
  "drive.sheets": "For Google Sheets only the first sheet is read.",
  "drive.choose": "Choose files from Google Drive",
  "drive.tooMany": "You picked {n} files. Nemo takes up to {max} at a time. Pick fewer, then pick again to add the rest.",
  "drive.progress": "Imported {done} of {total} files…",
  "drive.progress.some": "{done} of {total} files are ready. Open the list to see why the others are not.",
  "drive.progress.all": "All {total} files are ready.",
  "dfold.title": "Google Drive folder (shared with Nemo)",
  "dfold.help": "For a folder with many documents: share it with Nemo and every file inside it, including new ones, is read every day. Nemo reads only this folder and the folders inside it.",
  "dfold.link": "Link of your Google Drive folder",
  "dfold.continue": "Continue",
  "dfold.working": "One moment…",
  "dfold.pendingTitle": "Folder waiting to be verified",
  "dfold.step1": "1. In Google Drive, share the folder as Viewer with this address:",
  "dfold.step2": "2. Inside that folder, create an empty Google Doc (or any file) named exactly:",
  "dfold.step2why": "This proves the folder is yours; only someone who can add files to it can do this. You can delete it afterwards.",
  "dfold.step3": "3. Press Verify.",
  "dfold.copyAddress": "Copy address",
  "dfold.copyName": "Copy name",
  "dfold.copied": "Copied",
  "dfold.verify": "Verify",
  "dfold.cancel": "Cancel",
  "dfold.verified": "Folder verified. Now use it as a source of this agent.",
  "dfold.status.active": "Verified",
  "dfold.use": "Use this folder as a source",
  "dfold.added": "Folder added. Reading it now.",
  "dfold.stop": "Stop using this folder",
  "dfold.stopHint": "To stop Nemo reading it for good, also stop sharing the folder in Google Drive. What the agent already read stays until you remove the source.",
  "dfold.workspace": "If your organisation does not let you share with outside addresses, ask your Google Workspace administrator to allow this address, or choose files with the button above instead.",
  "dfold.sources": "Google Drive folders in use",
  "dfold.privacy": "You decide what to share. Nemo reads only the folder you verified; stop sharing it in Google Drive to cut access. What is read is stored and used to answer anyone who chats with this agent, so do not share confidential files.",
  "dfold.err.not_enabled": "Sharing a folder is not switched on yet.",
  "dfold.err.held": "Sharing a folder is paused for your account. Contact support.",
  "dfold.err.bad_link": "That does not look like a Google Drive folder link.",
  "dfold.err.cannot_open": "We could not open this folder. Check that you shared it with the address shown and that the file named above is inside it, then try again.",
  "dfold.err.expired": "The code expired. Cancel and paste the link again to get a new one.",
  "dfold.err.not_pending": "This folder is already verified or was removed.",
  "dfold.err.too_many_attempts": "Too many tries. Wait an hour and try again.",
  "dfold.err.too_many_pending": "You have many folders waiting. Finish or cancel some first.",
  "dfold.err.not_found": "We could not find that folder.",
  "dfold.err.folder_not_ready": "This folder is not verified. Verify it first.",
  "dfold.err.already_added": "This folder is already a source of this agent.",
  "dfold.err.forbidden": "Only account owners and managers can do this.",
  "dfold.err.limit_reached": "The agent has reached its limit of training documents.",
  "dfold.err.other": "Something went wrong. Try again.",
  "drive.added": "Added {n} file(s). Reading them now.",
  "drive.sources": "Google Drive sources",
  "drive.files": "Files in this source",
  "drive.showFiles": "Show files",
  "drive.hideFiles": "Hide files",
  "drive.syncNow": "Update now",
  "drive.remove": "Remove",
  "drive.removeSource": "Remove {name}",
  "drive.lastSync": "Last updated {when}.",
  "drive.neverSynced": "Not updated yet.",
  "drive.importedOnce": "Read once; not kept up to date.",
  "drive.source.pending": "Waiting",
  "drive.source.indexing": "Reading…",
  "drive.source.indexed": "Ready",
  "drive.source.error": "Needs attention",
  "drive.source.unsupported": "Not available",
  "drive.file.indexed": "Ready",
  "drive.file.pending": "Waiting",
  "drive.file.not_found": "Not found",
  "drive.file.no_access": "No access",
  "drive.file.unsupported": "Not supported",
  "drive.file.failed": "Could not read",
  "drive.disconnect": "Disconnect Google Drive",
  "drive.disconnect.title": "What should happen to what the agent already read from Google Drive?",
  "drive.disconnect.keep": "Keep it (it stops being updated)",
  "drive.disconnect.remove": "Remove it from the agent",
  "drive.disconnect.confirm": "Disconnect",
  "drive.cancel": "Cancel",
  "drive.disconnected.kept": "Disconnected. What the agent read is kept but no longer updated.",
  "drive.disconnected.removed": "Disconnected. What the agent read from Google Drive was removed.",
  "drive.result.connected": "Google Drive is connected. Now choose the files.",
  "drive.result.denied": "You did not give access, so nothing was connected.",
  "drive.result.missing_scope": "The permission to open the files you choose was not given. Connect again and leave that box ticked.",
  "drive.result.bad_state": "That sign-in took too long or was already used. Start again.",
  "drive.result.provider_failed": "Google did not answer. Try again in a few minutes.",
  "drive.result.held": "Google Drive is paused for your account. Contact support.",
  "drive.result.not_enabled": "Google Drive is not switched on yet.",
  "drive.err.not_connected": "Connect Google Drive first.",
  "drive.err.needs_reconnect": "The connection stopped working. Connect again.",
  "drive.err.held": "Google Drive is paused for your account. Contact support.",
  "drive.err.limit_reached": "The agent has reached its limit of training documents.",
  "drive.err.too_soon": "This was updated a moment ago. Try again in a few minutes.",
  "drive.err.forbidden": "Only account owners and managers can do this.",
  "drive.err.not_enabled": "Google Drive is not switched on yet.",
  "drive.err.rate_limited": "Too many tries. Wait a moment and try again.",
  "drive.err.other": "Something went wrong. Try again.",
  "check.viewport.ok": "The page is set up for phones (viewport setting found).",
  "check.viewport.missing": "This page has no mobile viewport setting, so on phones the page is shown tiny and zoomed out, and the chat looks tiny too and cannot use the full screen. Ask whoever manages the site to add <meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"> inside <head>.",
  "check.viewport.no_width": "This page's mobile viewport setting ({found}) does not say width=device-width, so on phones the page and the chat may look tiny. Ask whoever manages the site to use content=\"width=device-width, initial-scale=1\".",
  "embed.viewport": "Your page also needs this line inside its <head> so phones show it (and the chat) at the right size: <meta name=\"viewport\" content=\"width=device-width, initial-scale=1\">. Most website builders add it already; use \"Check my website\" below to find out.",
  "check.display.box": "The page has a box (id {id}): the chat will appear inside it.",
  "check.display.bubble": "The page has no chat box: the chat will appear as a floating bubble.",
  "check.display.bubble_fallback": "This agent is set to show inside a page, but this page has no box with id {id}, so it shows as a floating bubble.",
  "check.tag_manager.gtm_present": "This page uses Google Tag Manager. If you added the line there, this check cannot see it. Look at the page in a private window to be sure.",
  "topics.title": "Topics",
  "topics.examples": "Add an example topic:",
  "topics.placeholder": "Replace the [bracketed] part of the answer with your own details.",
  "topics.name": "Topic name",
  "topics.name.help": "Only you see this.",
  "topics.triggers": "Trigger phrases",
  "topics.addTrigger": "Add phrase",
  "topics.trigger.placeholder": "e.g. opening hours",
  "topics.answer": "Exact answer",
  "topics.answer.help": "Sent word for word. Keep it short; put longer material in your knowledge. Write web addresses in full (https://…).",
  "topics.on": "Topic is on",
  "topics.untitled": "New topic {n}",
  "topics.shadowed": "“{trigger}” is already covered by the topic “{by}” above, which answers first.",
  "topics.oversize": "This topic is larger than recommended. It keeps working; shorten it when you edit.",
  "topics.incomplete": "Not saved yet: a topic needs a name, a phrase and an answer.",
  "topics.up": "Move {title} up",
  "topics.down": "Move {title} down",
  "topics.moved": "{title} moved to position {n}",
  "topics.remove": "Remove topic",
  "topics.add": "Add topic",
  "topics.test": "Test the topics",
  "topics.test.help": "Type what a visitor might write. This uses the same matching as the chat; nothing is sent to the AI.",
  "topics.test.hit": "Topic “{title}” would answer:",
  "topics.test.miss": "No topic matches. The AI would answer from your knowledge.",
  "ocr.button": "Read scanned pages",
  "ocr.buttonFor": "Read the scanned pages of {name}",
  "ocr.checking": "Checking…",
  "ocr.group": "Reading scanned pages of {name}",
  "ocr.summary": "{n} scanned pages to read, about {usd} of AI usage, about {min} min. Nothing starts until you press Start.",
  "ocr.start": "Start reading",
  "ocr.cancel": "Cancel",
  "ocr.stop": "Stop reading",
  "ocr.changed": "The price changed. Look at the new estimate and start again.",
  "ocr.failed": "That could not be done right now. Try again in a moment.",
  "ocr.r.ocr_off": "Reading scanned pages is switched off at the moment.",
  "ocr.r.no_reader": "Reading scanned pages is not available on this server.",
  "ocr.r.not_pdf": "Only PDF documents can be read this way.",
  "ocr.r.nothing_to_read": "This document has no scanned pages to read.",
  "ocr.r.too_many_pages": "This document has more scanned pages than one run may read. Split it into parts.",
  "ocr.r.day_cap": "The daily limit for reading scanned pages has been reached. Try again tomorrow.",
  "ocr.r.month_cap": "The monthly limit for reading scanned pages has been reached.",
  "ocr.r.running": "This document is already being read.",
  "ocr.r.unreadable": "This PDF could not be opened (it may be damaged or protected).",
  "docs.title": "Training documents",
  "docs.help": "Up to {max} files per agent, {mb} MB each: PDF, Word (.docx), Excel (.xlsx), CSV and text. Scanned PDFs (pictures of pages) cannot be read yet. Old .doc and .xls files and slides: save as .docx, .xlsx or PDF first.",
  "docs.upload": "Upload files",
  "docs.upload.label": "Upload training documents",
  "docs.stop": "Stop",
  "docs.uploading": "Uploading {done} of {total}…",
  "docs.removing": "Removing {done} of {total}…",
  "docs.retrying": "Retrying {done} of {total}…",
  "docs.progress": "Documents trained so far",
  "docs.summary": "{total} documents: {ready} ready, {working} in progress, {attention} need attention.",
  "docs.search": "Search by name",
  "docs.filter": "Show",
  "docs.f.all": "All",
  "docs.f.ready": "Ready",
  "docs.f.working": "In progress",
  "docs.f.attention": "Needs attention",
  "docs.f.unsupported": "Not supported",
  "docs.retryAll": "Retry all that need attention ({n})",
  "docs.none": "No documents match.",
  "docs.selectPage": "Select all on this page",
  "docs.selectAll": "Select all {n} that match",
  "docs.bulk": "Actions for the selected documents",
  "docs.selected": "{n} selected",
  "docs.removeSel": "Remove selected",
  "docs.clear": "Clear selection",
  "docs.confirm": "Remove {n} documents? This cannot be undone.",
  "docs.confirmYes": "Yes, remove",
  "docs.keep": "Keep them",
  "docs.select": "Select {name}",
  "docs.remove": "Remove {name}",
  "docs.removeOne": "Remove",
  "docs.retry": "Retry {name}",
  "docs.retryOne": "Retry",
  "docs.passages": "{n} passages",
  "docs.s.pending": "Waiting",
  "docs.s.indexing": "Training…",
  "docs.s.indexed": "Ready",
  "docs.s.error": "Needs attention",
  "docs.s.unsupported": "Not supported",
  "docs.pages": "Pages of documents",
  "docs.showing": "Showing {from}–{to} of {n}",
  "docs.prev": "Previous",
  "docs.next": "Next",
  "docs.page": "Page {p} of {pages}",
  "docs.tooBig": "{name}: files can be up to {mb} MB.",
  "docs.badType": "{name}: this file type is not supported. Use PDF, Word (.docx), Excel (.xlsx), CSV or text.",
  "docs.failedOne": "{name}: could not be added.",
  "docs.limit": "You have reached the limit of {max} documents for this agent. The rest were not added.",
  "docs.skipped": "{n} files were not added. First: {first}",
  "docs.someFailed": "{n} could not be done. Try again.",
  "example.group": "Examples",
  "example.label": "Need a start?",
  "example.general": "Insert example: general",
  "example.service": "Insert example: service business",
  "example.replace": "Replace what you wrote?",
  "example.yes": "Replace",
  "example.no": "Keep mine",
  "ready.title": "Ready to publish?",
  "ready.count": "{n} of {total} done",
  "ready.advice": "Advice only: you can publish at any time.",
  "ready.s.done": "Done",
  "ready.s.tip": "Could be better",
  "ready.s.todo": "Not done yet",
  "ready.go": "Go to the setting",
  "ready.try": "Open the preview",
  "ready.looksgood": "Looks good",
  "ready.warnings": "Things to look at",
  "ready.device": "The \"Looks good\" ticks are saved on this device only.",
  "fix.title": "The chat does not show on my website?",
  "fix.intro": "The most common reasons, and what to do:",
  "fix.domain.q": "The site is not on the allowed list.",
  "fix.domain.a": "A site with and without www counts as two. Use Check my website above and press Allow, then Publish.",
  "fix.place.q": "The line is in the wrong place or not saved.",
  "fix.place.a": "Paste it once, at the end of the page code on every page, then save and publish the site (Wix and Squarespace only show changes after you publish).",
  "fix.published.q": "The agent is not published.",
  "fix.published.a": "Press Publish in Nemo. Until then visitors see nothing.",
  "fix.plan.q": "Your plan limit is used up.",
  "fix.plan.a": "The chat pauses until the next period or until you upgrade. You will see a notice in Nemo.",
  "fix.blocker.q": "A visitor's ad blocker or privacy mode hides it.",
  "fix.blocker.a": "Some visitors will not see the chat. Share the public chat link as a fallback.",
  "fix.csp.q": "Your site's security settings block it.",
  "fix.csp.a": "Check my website tells you which address to allow; send it to whoever manages the site.",
  "fix.banner.q": "A cookie banner covers the bubble.",
  "fix.banner.a": "Lift the bubble: add data-bottom-offset to the line, for example 80 (pixels), as below.",
  "fix.doctor": "To see exactly why, open your site with #nemo-doctor at the end of the address (for example www.mysite.com/#nemo-doctor), then open the browser console (F12). Nemo prints one plain line per finding. To lift the bubble above a banner:",
  "fix.lift.label": "Example line with the bubble lifted",
  "check.builder.wix": "This looks like a Wix site. For the floating bubble use Settings > Custom Code (Body - end, All pages). For a chat inside a page use the Public chat link with Add > Embed Code > Embed a site.",
  "check.builder.squarespace": "This looks like a Squarespace site. Use Settings > Developer Tools > Code Injection > Footer. For a chat inside a page, use the Public chat link in an Embed block.",
  "check.builder.shopify": "This looks like a Shopify store. Use Online Store > Themes > Edit code > theme.liquid, just before </body>.",
  "check.builder.wordpress": "This looks like a WordPress site. Use the WPCode plugin (Code Snippets > Header & Footer > Footer).",
  "check.builder.webflow": "This looks like a Webflow site. Use Project Settings > Custom Code > Footer Code, then publish the site.",
  "check.status.ok": "OK",
  "check.status.fail": "Needs fixing",
  "check.status.warn": "Check this",
  "check.status.info": "Note",
  "legal.title": "Legal notices",
  "legal.help": "Shown after the greeting, in this order. Each one is a short text (up to 500 characters) or a link to a page such as your privacy policy. Up to {max}.",
  "legal.none": "No legal notices.",
  "legal.n": "Notice {n}",
  "legal.type": "Type of notice {n}",
  "legal.text": "Text",
  "legal.link": "Link",
  "legal.placeholderText": "Type the legal text to show to visitors…",
  "legal.up": "Move notice {n} up",
  "legal.down": "Move notice {n} down",
  "legal.remove": "Remove notice {n}",
  "legal.add": "Add a notice",
  "avatar.upload": "Upload a picture",
  "avatar.replace": "Replace picture",
  "avatar.adjust": "Adjust",
  "avatar.remove": "Remove picture",
  "avatar.drop": "Drop a picture here, or choose one from your computer or phone.",
  "avatar.rules": "JPG, PNG or WebP, up to 5 MB. A face or logo that fills the frame works best.",
  "avatar.fileInput": "Choose a picture file",
  "avatar.alt": "Picture of {name}",
  "avatar.thisAgent": "this agent",
  "avatar.frame": "Picture frame. Drag the picture to move it, or use the arrow keys. Plus and minus change the size.",
  "avatar.help": "Drag the picture, use the arrow keys (hold Shift for bigger steps), the mouse wheel or two fingers to zoom.",
  "avatar.zoom": "Zoom",
  "avatar.preview": "This is how it looks in the chat:",
  "avatar.save": "Use this picture",
  "avatar.saving": "Saving…",
  "avatar.saved": "Picture saved. It shows in the chat after you publish.",
  "avatar.removed": "Picture removed. The chat shows the default.",
  "avatar.or": "Or use a picture from the internet",
  "avatar.urlLabel": "Picture address",
  "avatar.urlHelp": "Optional. Paste the address of a square picture.",
  "avatar.err.too_large": "That file is too large. Choose a picture under 5 MB.",
  "avatar.err.too_many_pixels": "That picture is too big. Choose one smaller than about 6000 by 5000 pixels.",
  "avatar.err.unsupported_type": "We can only use JPG, PNG or WebP pictures (not SVG or GIF).",
  "avatar.err.animated": "Animated pictures are not accepted. Choose a still picture.",
  "avatar.err.too_small": "That picture is too small. Choose one at least 64 pixels on each side.",
  "avatar.err.corrupt": "We could not read that picture. Try another one.",
  "avatar.err.no_file": "Choose a picture first.",
  "avatar.err.rate_limited": "Too many pictures in a short time. Wait a minute and try again.",
  "avatar.err.no_source": "There is no picture to adjust. Upload one first.",
  "avatar.err.busy": "Many pictures are being processed right now. Try again in a few seconds.",
  "avatar.err.generic": "Could not save the picture. Try again.",
} as const;

export type Key = keyof typeof en;

/** Is there an English entry for this key? (Used to look up settings by path and fall back.) */
export const hasKey = (key: string): key is Key => key in en;

const es: Partial<Record<Key, string>> = {
  "lang.label": "Idioma",
  "common.email": "Correo",
  "common.password": "Contraseña",
  "common.name": "Tu nombre",
  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.saving": "Guardando…",
  "common.saved": "Guardado.",
  "common.loading": "Cargando…",
  "common.remove": "Quitar",
  "common.back": "Volver",
  "common.copy": "Copiar",
  "common.or": "o",
  "common.network": "No pudimos conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.",
  "common.generic": "Algo salió mal de nuestro lado. Inténtalo de nuevo en un momento.",
  "login.title": "Inicia sesión en Nemo",
  "login.submit": "Iniciar sesión",
  "login.busy": "Entrando…",
  "login.forgot": "¿Olvidaste tu contraseña?",
  "login.noAccount": "¿Eres nuevo en Nemo?",
  "login.create": "Crear una cuenta",
  "login.google": "Continuar con Google",
  "login.apiKey": "Iniciar sesión con una clave de API",
  "login.googleFailed": "No pudimos iniciar sesión con Google. Inténtalo de nuevo o usa tu correo y contraseña.",
  "login.notStaff": "Esa cuenta de Google no tiene acceso al área de administración.",
  "login.sessionEnded": "Tu sesión terminó. Inicia sesión de nuevo; tus últimos cambios se guardaron.",
  "login.verifyFirst": "Primero confirma tu correo. Te enviamos un enlace.",
  "login.resend": "Enviar el enlace otra vez",
  "login.resent": "Si esa dirección necesita confirmación, enviamos un enlace nuevo.",
  "signup.title": "Crea tu cuenta de Nemo",
  "signup.lead": "Crea un agente de chat para tu sitio web en pocos minutos. No necesitas tarjeta para empezar.",
  "signup.business": "Nombre de tu negocio o sitio web",
  "signup.businessHelp": "Opcional. Puedes cambiarlo después.",
  "signup.passwordHelp": "Al menos 10 caracteres. Una frase corta funciona bien.",
  "signup.submit": "Crear cuenta",
  "signup.busy": "Creando…",
  "signup.haveAccount": "¿Ya tienes una cuenta?",
  "signup.terms": "Al crear una cuenta aceptas los términos y la política de privacidad de nuestro sitio web.",
  "check.title": "Revisa tu correo",
  "check.body": "Enviamos un enlace a {email}. Ábrelo para confirmar tu dirección y empezar. El enlace funciona una sola vez y caduca en 24 horas.",
  "check.spam": "¿No llega? Mira en la carpeta de spam, o",
  "check.again": "envía el enlace otra vez",
  "forgot.title": "Restablece tu contraseña",
  "forgot.lead": "Escribe tu correo y te enviaremos un enlace para elegir una contraseña nueva.",
  "forgot.submit": "Enviarme el enlace",
  "forgot.done": "Si esa dirección tiene una cuenta, enviamos un enlace. Funciona una sola vez y caduca en 1 hora.",
  "reset.title": "Elige una contraseña nueva",
  "reset.submit": "Guardar contraseña nueva",
  "reset.done": "Tu contraseña cambió. Se cerró tu sesión en todos lados; entra con la nueva.",
  "verify.working": "Confirmando tu correo…",
  "verify.failed": "Este enlace ya no es válido. Inicia sesión y te enviaremos uno nuevo.",
  "invite.title": "Te invitaron",
  "invite.body": "Únete a “{account}” como {role}.",
  "invite.newPerson": "Crea tu acceso para unirte. Tu correo es {email}.",
  "invite.signInFirst": "{email} ya tiene acceso a Nemo. Inicia sesión con ese correo para aceptar la invitación.",
  "invite.accept": "Aceptar invitación",
  "invite.invalid": "Esta invitación ya no es válida. Pide una nueva.",
  "role.owner": "Propietario",
  "role.manager": "Administrador",
  "role.member": "Miembro",
  "role.owner.help": "Todo, incluida la cuenta y su facturación.",
  "role.manager.help": "Publicar agentes, gestionar claves de API y el equipo (salvo propietarios).",
  "role.member.help": "Crear y probar agentes y ver contactos. No puede publicar ni gestionar el equipo.",
  "nav.agents": "Agentes",
  "nav.leads": "Contactos",
  "nav.notifications": "Avisos",
  "nav.team": "Equipo",
  "nav.account": "Cuenta",
  "nav.keys": "Claves de API",
  "nav.signout": "Cerrar sesión",
  "switcher.label": "Cuenta",
  "switcher.new": "Crear otra cuenta…",
  "switcher.newName": "Nombre de la cuenta nueva",
  "onboarding.title": "Te damos la bienvenida a Nemo, {name}",
  "onboarding.lead": "Vamos a crear tu primer agente. Responde las preguntas de tus visitantes con lo que le enseñes, en tu sitio web, a cualquier hora.",
  "onboarding.step1": "1. Ponle nombre y dile qué hace",
  "onboarding.step2": "2. Enséñale (tu sitio web, archivos, preguntas)",
  "onboarding.step3": "3. Pruébalo y publícalo",
  "onboarding.agentName": "Nombre de tu agente",
  "onboarding.agentNameHelp": "Hasta 20 caracteres. Lo verán tus visitantes.",
  "onboarding.job": "¿En qué debe ayudar?",
  "onboarding.jobHelp": "Por ejemplo: responder preguntas sobre nuestros precios y horarios.",
  "onboarding.start": "Crear mi agente",
  "onboarding.aura": "¿Prefieres que te guíen? Aura, nuestra asistente que crea el agente contigo, llegará pronto.",
  "onboarding.unverified": "Confirma tu correo (te enviamos un enlace) antes de publicar.",
  "team.title": "Equipo",
  "team.lead": "Las personas que pueden trabajar en esta cuenta. Cada una entra con su propio correo.",
  "team.you": "(tú)",
  "team.invite": "Invitar a alguien",
  "team.inviteEmail": "Su correo",
  "team.inviteRole": "Rol",
  "team.send": "Enviar invitación",
  "team.sending": "Enviando…",
  "team.sent": "Invitación enviada a {email}.",
  "team.pending": "Pendientes de unirse",
  "team.pendingNone": "No hay invitaciones pendientes.",
  "team.expires": "caduca el {date}",
  "team.revoke": "Cancelar invitación",
  "team.changeRole": "Rol de {name}",
  "team.remove": "Quitar",
  "team.removeConfirm": "¿Quitar a {name} de esta cuenta? Pierde el acceso de inmediato.",
  "team.leave": "Salir de esta cuenta",
  "team.leaveConfirm": "¿Salir de “{account}”? Perderás el acceso a sus agentes.",
  "team.lastOwner": "Una cuenta necesita al menos un propietario.",
  "team.cannotInvite": "Solo los propietarios y administradores pueden invitar.",
  "team.members": "Miembros",
  "team.verifyFirst": "Confirma tu propio correo antes de invitar a otras personas.",
  "account.title": "Cuenta",
  "account.you": "Tú",
  "account.profile": "Tu perfil",
  "account.language": "Idioma",
  "account.languageHelp": "Se usa en las pantallas de esta sección y en los correos que te enviamos.",
  "account.password": "Cambiar contraseña",
  "account.currentPassword": "Contraseña actual",
  "account.newPassword": "Contraseña nueva",
  "account.passwordChanged": "Contraseña cambiada. Se cerró la sesión en tus otros dispositivos.",
  "account.noPassword": "Entras con Google, así que no hay contraseña que cambiar.",
  "account.sessions": "Dónde has iniciado sesión",
  "account.sessionsLead": "Si ves un dispositivo que no conoces, termina esa sesión y cambia tu contraseña.",
  "account.thisDevice": "Este dispositivo",
  "account.endSession": "Terminar sesión",
  "account.signOutAll": "Cerrar sesión en todos lados",
  "account.workspace": "Esta cuenta",
  "account.workspaceName": "Nombre de la cuenta",
  "account.onlyOwner": "Solo un propietario puede cambiar estos ajustes.",
  "account.plan": "Plan",
  "account.delete": "Eliminar esta cuenta",
  "account.deleteWarn": "Esto oculta la cuenta y todo lo que contiene de inmediato: los agentes dejan de responder, las claves de API dejan de funcionar y nadie puede entrar. Guardamos los datos {days} días por si cambias de opinión (escríbenos y podemos restaurarla); después se borran para siempre. Enviamos a los propietarios un enlace para obtener una copia de tus datos (agentes, conocimiento, conversaciones y contactos) y dos recordatorios. El enlace de descarga funciona 15 días.",
  "account.deleteConfirm": "Para confirmar, escribe el nombre de la cuenta exactamente: {name}",
  "account.deleteButton": "Eliminar la cuenta",
  "account.deleted": "La cuenta fue eliminada.",
  "upgrade.cta": "Ver planes",
  "upgrade.title": "Tu plan llegó a su límite",
  "keys.note": "Las claves de API son solo para conectar tus propios sistemas. No necesitas una para usar Nemo aquí.",
  "keys.forbidden": "Solo los propietarios y administradores pueden gestionar claves de API.",
  "mfa.challengeTitle": "Escribe tu código",
  "mfa.challengeLead": "Abre tu aplicación de autenticación y escribe el código de 6 dígitos de Nemo.",
  "mfa.code": "Código de 6 dígitos",
  "mfa.verify": "Continuar",
  "mfa.useRecovery": "Perdí mi teléfono: usar un código de recuperación",
  "mfa.useApp": "Usar el código de mi aplicación",
  "mfa.recoveryCode": "Código de recuperación",
  "mfa.recoveryLeft": "Códigos de recuperación restantes: {n}. Crea un juego nuevo en Cuenta, Seguridad.",
  "mfa.setupTitle": "Activa la verificación en dos pasos",
  "mfa.setupLeadRequired": "Para proteger tu cuenta, la verificación en dos pasos es obligatoria. Toma dos minutos.",
  "mfa.setupLead": "La verificación en dos pasos pide un código de tu teléfono cada vez que inicias sesión, así una contraseña robada no basta.",
  "mfa.step1": "1. Instala una aplicación de autenticación (Google Authenticator, Microsoft Authenticator, 1Password, Authy…).",
  "mfa.step2": "2. En la aplicación toca “Añadir cuenta” (o “+”), elige “Escanear código QR” y apunta la cámara a este código:",
  "mfa.qrAlt": "Código QR que añade Nemo a tu aplicación de autenticación",
  "mfa.noScan": "¿No puedes escanearlo? Introduce una clave a mano",
  "mfa.manual": "En la aplicación elige “Introducir una clave de configuración” y escribe esta clave:",
  "mfa.openApp": "¿Estás en este teléfono? Ábrela directamente en tu aplicación",
  "mfa.step3": "3. Escribe el código de 6 dígitos que muestra la aplicación ahora:",
  "mfa.start": "Empezar",
  "mfa.savedTitle": "Guarda tus códigos de recuperación",
  "mfa.savedLead": "Si pierdes el teléfono, cada uno de estos códigos te deja entrar una vez. Guárdalos en un lugar seguro (un gestor de contraseñas o una hoja impresa). No podremos mostrarlos otra vez.",
  "mfa.savedCheck": "Guardé mis códigos de recuperación",
  "mfa.done": "Listo",
  "mfa.security": "Verificación en dos pasos",
  "mfa.on": "Activada. Te pediremos un código cada vez que inicies sesión.",
  "mfa.off": "Desactivada. Actívala para proteger tu cuenta con tu teléfono.",
  "mfa.turnOn": "Activar",
  "mfa.turnOff": "Desactivar",
  "mfa.turnOffLead": "Escribe un código actual de tu aplicación para desactivarla.",
  "mfa.requiredNote": "Es obligatoria para tu cuenta; no se puede desactivar.",
  "mfa.newCodes": "Crear códigos de recuperación nuevos",
  "mfa.newCodesNote": "Los códigos anteriores dejan de funcionar.",
  "mfa.signOut": "Cerrar sesión",
  "mfa.nudgeFirst": "Protege tu cuenta: activa la verificación en dos pasos. Pide un código de tu teléfono al iniciar sesión, así una contraseña robada no basta. Toma dos minutos.",
  "mfa.nudgeTrial": "Terminó tu prueba gratuita. Ahora que tus agentes trabajan para tu negocio, te recomendamos activar la verificación en dos pasos.",
  "mfa.nudgeSetup": "Activar ahora",
  "mfa.nudgeLater": "Ahora no",
  "export.title": "Obtén una copia de tus datos",
  "export.lead": "La cuenta “{account}” fue eliminada. Guardamos sus datos hasta el {date}; después se borran para siempre.",
  "export.what": "Puedes pedir una copia ahora: un archivo con tus agentes, conocimiento y conversaciones, y una hoja con tus contactos. Enviamos por correo un enlace de descarga a los propietarios cuando esté lista. El enlace funciona 15 días.",
  "export.button": "Enviarme mis datos",
  "export.sending": "Preparando…",
  "export.sent": "Listo. Enviamos el enlace de descarga a los propietarios de la cuenta. Revisa tu bandeja de entrada (y el spam).",
  "export.invalid": "Este enlace ya no es válido. Puede que los datos ya se hayan borrado.",
  "export.tooMany": "Ya enviamos el enlace varias veces. Revisa tu bandeja y la carpeta de spam, o inténtalo de nuevo en una hora.",
  "download.title": "Descarga tus datos",
  "download.lead": "Dos archivos: todo en uno, y tus contactos en una hoja de cálculo. Guárdalos en un lugar seguro: contienen datos de tus clientes.",
  "download.json": "Descargar todo (JSON)",
  "download.csv": "Descargar contactos (hoja de cálculo)",
  "download.invalid": "Este enlace de descarga caducó o no es válido.",
  "signup.inviteOnlyTitle": "Nemo es solo por invitación por ahora",
  "signup.inviteOnlyBody": "Estamos dejando entrar a la gente por invitación mientras nos preparamos. Si tienes una invitación, abre el enlace que te enviamos por correo. Si no la tienes, escríbenos y te añadimos.",
  "signup.domainsLead": "Nemo es solo por invitación por ahora. Las direcciones que terminan en {domains} pueden crear una cuenta (confirma tu correo y luego activa la verificación en dos pasos); las demás necesitan una invitación.",
  "signup.closedTitle": "Nemo no acepta cuentas nuevas por ahora",
  "signup.closedBody": "Las cuentas nuevas están en pausa. Si ya tienes una cuenta, puedes iniciar sesión.",
  "signup.invalidInvite": "Esta invitación ya no es válida",
  "signup.invitedLead": "Te invitaron. Crea tu cuenta con {email}.",
  "signup.invitedEmailHelp": "Es la dirección a la que se envió tu invitación.",
  "login.signupClosed": "Nemo es solo por invitación por ahora: una cuenta nueva necesita una invitación. Si ya tienes una cuenta, inicia sesión con la misma dirección de Google que usaste antes.",
  "act.link": "Historial",
  "act.title": "Historial de {name}",
  "act.back": "Volver al agente",
  "act.tab.activity": "Actividad",
  "act.tab.versions": "Versiones",
  "act.lead": "Quién creó, cambió y publicó este agente, y cuándo. Solo los propietarios y administradores pueden verlo.",
  "act.forbidden": "Solo los propietarios y administradores pueden ver el historial de un agente.",
  "act.empty": "Todavía no hay nada registrado.",
  "act.noMatch": "Nada coincide con estos filtros.",
  "act.filter.action": "Qué pasó",
  "act.filter.any": "Cualquier cosa",
  "act.filter.person": "Quién",
  "act.filter.from": "Desde",
  "act.filter.to": "Hasta",
  "act.more": "Mostrar entradas anteriores",
  "act.who.system": "Nemo (automático)",
  "act.who.partner": "Una conexión de partner",
  "act.who.key": "Clave de API “{name}”",
  "act.via": "desde {client}",
  "act.action.created": "{who} creó este agente",
  "act.action.edited": "{who} cambió {what}",
  "act.action.published": "{who} publicó la versión {n}",
  "act.action.publishedFirst": "{who} publicó la versión {n} (la primera)",
  "act.action.unpublished": "{who} dejó el agente fuera de línea (despublicado)",
  "act.action.reverted": "{who} devolvió al borrador los ajustes de la versión {n}",
  "act.action.deleted": "{who} eliminó este agente",
  "act.action.source_added": "{who} añadió conocimiento: {what}",
  "act.action.source_removed": "{who} quitó conocimiento: {what}",
  "act.action.transferred": "{who} movió este agente",
  "act.action.shared": "{who} compartió este agente",
  "act.action.unshared": "{who} dejó de compartir este agente",
  "act.andMore": "{list} y {n} más",
  "act.changedSet": "definido",
  "act.changedCleared": "borrado",
  "act.chars": "{n} caracteres",
  "act.items": "{n} elementos",
  "act.fromTo": "{from} → {to}",
  "act.changedOnly": "cambiado",
  "act.src.text": "texto",
  "act.src.file": "un archivo",
  "act.src.url": "una página web",
  "act.src.crawl": "un sitio web",
  "act.src.other": "una fuente",
  "act.f.name": "el nombre",
  "act.f.creatorName": "el nombre del creador",
  "act.f.avatarUrl": "el avatar",
  "act.f.persona": "el trabajo y la personalidad",
  "act.f.responseInstructions": "las instrucciones de respuesta",
  "act.f.greetings": "los saludos",
  "act.f.conversationStarters": "las preguntas iniciales",
  "act.f.strictness": "la rigurosidad",
  "act.f.creativity": "la creatividad",
  "act.f.model.generative.model": "el modelo de redacción",
  "act.f.model.generative.provider": "el proveedor del modelo",
  "act.f.knowledge.faqs": "las preguntas y respuestas",
  "act.f.knowledge.topics": "los temas",
  "act.f.leadCapture.enabled": "la recogida de datos de contacto",
  "act.f.leadCapture.fields": "los datos de contacto que se piden",
  "act.f.leadCapture.notifyEmails": "a quién se avisa de los contactos",
  "act.f.leadCapture.crm": "el destino de los contactos",
  "act.f.leadCapture.flow": "cómo pide el agente los datos",
  "act.f.deployment.website.theme": "el color",
  "act.f.deployment.website.mode": "cómo aparece el chat en la página",
  "act.f.deployment.website.position": "la posición",
  "act.f.deployment.website.style": "el estilo",
  "act.f.deployment.website.language": "el idioma del chat",
  "act.f.deployment.website.allowedDomains": "los sitios web permitidos",
  "act.f.deployment.website.brandName": "el nombre de marca",
  "act.f.deployment.website.voiceInput": "la entrada de voz",
  "act.f.deployment.website.attribution.enabled": "el seguimiento de origen",
  "act.f.deployment.website.publicChat": "el enlace de chat público",
  "act.f.legalNotice": "el aviso legal",
  "act.f.allowModelTraining": "permitir el entrenamiento del modelo",
  "act.f.other": "otros ajustes",
  "act.v.lead": "Cada vez que publicas, esa versión se conserva. Puedes devolver al borrador los ajustes de una anterior para revisarlos, y publicar de nuevo si quieres.",
  "act.v.empty": "Este agente todavía no se ha publicado.",
  "act.v.version": "Versión",
  "act.v.when": "Publicada",
  "act.v.by": "Por",
  "act.v.live": "En vivo ahora",
  "act.v.revert": "Volver a esta versión",
  "act.v.revertAria": "Volver a la versión {n}",
  "act.v.confirm": "¿Devolver al borrador los ajustes de la versión {n}? No se publica nada y lo que está en vivo no cambia. Tu borrador actual se reemplaza (queda en el historial). Los archivos y sitios web que añadiste o quitaste desde entonces no forman parte de una versión y se quedan como están.",
  "act.v.confirmYes": "Sí, ponerlos en el borrador",
  "act.v.done": "Listo. Los ajustes de la versión {n} están en el borrador. Revísalos y publica cuando estés conforme.",
  "act.v.review": "Revisar el borrador",
  "act.v.failed": "No se pudo devolver esa versión: {why}",
  "act.a.created": "Creado",
  "act.a.edited": "Ajustes cambiados",
  "act.a.published": "Publicado",
  "act.a.unpublished": "Despublicado",
  "act.a.reverted": "Devuelto desde una versión",
  "act.a.deleted": "Eliminado",
  "act.a.source_added": "Conocimiento añadido",
  "act.a.source_removed": "Conocimiento quitado",
  "act.a.transferred": "Movido",
  "act.a.shared": "Compartido",
  "act.a.unshared": "Se dejó de compartir",
  "err.invalid_credentials": "El correo y la contraseña no coinciden. Revísalos e inténtalo de nuevo.",
  "err.forbidden": "Tu rol no permite esto. Pide ayuda a un propietario o administrador.",
  "err.not_a_member": "No perteneces a esa cuenta.",
  "err.account_suspended": "Esta cuenta está en pausa. Contacta a soporte.",
  "err.account_deleted": "Esta cuenta fue eliminada.",
  "share.title": "Compartir",
  "share.back": "← Volver al agente",
  "share.badge": "Compartido",
  "share.sharedBy": "{account} lo compartió contigo. Tu rol: {role}.",
  "share.ownedBy": "Este agente pertenece a “{account}”.",
  "share.people": "Personas con acceso",
  "share.accountTeam": "Más las {n} personas del equipo de la cuenta.",
  "share.noGuests": "Nadie más tiene acceso todavía.",
  "share.invite": "Compartir con alguien",
  "share.inviteHelp": "Tendrá acceso solo a este agente, no al resto de tu cuenta. Puede tener su propio acceso, incluso en otra empresa.",
  "share.email": "Su correo",
  "share.role": "Rol",
  "share.send": "Enviar invitación",
  "share.sending": "Enviando…",
  "share.sent": "Invitación enviada a {email}.",
  "share.pending": "Invitaciones pendientes",
  "share.pendingNone": "No hay invitaciones pendientes.",
  "share.revoke": "Cancelar invitación",
  "share.remove": "Quitar",
  "share.removeConfirm": "¿Quitar a {name}? Pierde el acceso de inmediato.",
  "share.leave": "Salir de este agente",
  "share.leaveConfirm": "¿Salir de “{agent}”? Dejarás de verlo.",
  "share.changeRole": "Rol de {name}",
  "share.expires": "caduca el {date}",
  "role.viewer": "Lector",
  "role.editor": "Editor",
  "role.viewer.help": "Puede ver el agente y probarlo. No puede cambiarlo.",
  "role.editor.help": "Puede cambiar el agente y agregar conocimiento. No puede publicar.",
  "role.manager.agentHelp": "Además puede publicar y compartir con lectores y editores.",
  "share.leadsNote": "Las personas con quienes compartes un agente nunca ven sus contactos ni a dónde se envían.",
  "transfer.peopleTitle": "Quién tiene acceso a este agente",
  "transfer.peopleNone": "Nadie más tiene acceso.",
  "transfer.peopleHelp": "Conservaron el acceso que tenían. Quita a quien no conozcas.",
  "transfer.manage": "Administrar el acceso",
  "plan.upgrade": "Ver planes",
  "transfer.overPlan": "Tu plan aún no tiene espacio para este agente, así que es de solo lectura: puedes verlo y probarlo, pero no cambiarlo ni publicarlo hasta que mejores tu plan o liberes espacio.",
  "share.readOnly": "Puedes ver este agente y probarlo, pero no cambiarlo.",
  "share.transfer": "Transferir la propiedad",
  "share.transferHelp": "Entrega este agente a otra persona. Ella elige a qué cuenta suya llega. Nada cambia hasta que acepte.",
  "share.transferMoves": "Se va con él: el agente y sus versiones, fuentes de conocimiento, conversaciones, contactos y su historial de envíos. El chat de tu sitio sigue funcionando.",
  "share.transferResets": "Cambia: los contactos nuevos se envían al correo del nuevo propietario en vez del tuyo, y se quita cualquier conexión con un CRM. Las personas con quienes lo compartiste conservan su acceso hasta que el nuevo propietario lo quite.",
  "share.transferEmail": "Correo del nuevo propietario",
  "share.transferConfirm": "Escribe el nombre del agente para confirmar: {name}",
  "share.transferKeep": "Conservar mi acceso después",
  "share.keep.none": "Sin acceso",
  "share.transferSend": "Enviar la oferta",
  "share.transferSent": "Oferta enviada a {email}. Caduca el {date}.",
  "share.transferOpen": "Oferta esperando a {email} (caduca el {date}).",
  "share.transferCancel": "Cancelar la oferta",
  "share.transferOnlyOwner": "Solo un propietario o administrador de la cuenta puede transferir este agente.",
  "shared.section": "Compartidos contigo",
  "shared.owner": "de {account}",
  "shared.open": "Abrir",
  "agents.share": "Compartir",
  "transfer.title": "Alguien quiere entregarte un agente",
  "transfer.body": "{sender} ({account}) quiere transferirte “{agent}”, con su conocimiento, conversaciones y contactos.",
  "transfer.choose": "¿A cuál de tus cuentas debe llegar?",
  "transfer.accept": "Aceptar y traerlo aquí",
  "transfer.decline": "Rechazar",
  "transfer.accepted": "Listo. “{agent}” ya está en tu cuenta. Los contactos nuevos ahora llegan a tu correo.",
  "transfer.declined": "Rechazaste la oferta.",
  "transfer.invalid": "Esta oferta ya no es válida.",
  "transfer.signInFirst": "Inicia sesión con {email} para ver esta oferta.",
  "transfer.noLogin": "Crea una cuenta con {email} (y confírmala), luego abre este enlace de nuevo.",
  "transfer.noAccounts": "Necesitas ser propietario o administrador de una cuenta para recibir un agente.",
  "transfer.pendingBanner": "Tienes {n} oferta(s) de agente esperando.",
  "transfer.review": "Revisar",
  "shareAccept.title": "Compartieron un agente contigo",
  "shareAccept.body": "Abre “{agent}” de “{account}” como {role}.",
  "shareAccept.newPerson": "Crea tu acceso para abrirlo. Tu correo es {email}.",
  "shareAccept.signInFirst": "{email} ya tiene acceso a Nemo. Inicia sesión con ese correo para aceptar.",
  "shareAccept.accept": "Aceptar",
  "shareAccept.invalid": "Esta invitación ya no es válida. Pide una nueva.",
  "nav.billing": "Facturación",
  "billing.title": "Facturación",
  "billing.lead": "Tu plan, lo que has usado este mes y tus facturas. Los pagos los gestiona Stripe de forma segura: Nemo nunca ve el número de tu tarjeta.",
  "billing.ownerOnly": "Solo el propietario de la cuenta puede ver la facturación.",
  "billing.unavailable": "La facturación aún no está activada.",
  "billing.current": "Tu plan",
  "billing.noPlan": "Todavía no tienes un plan. Puedes seguir construyendo; elige un plan para poner tu agente en tu sitio web.",
  "billing.state.trialing": "Prueba gratuita",
  "billing.state.active": "Activo",
  "billing.state.grace": "Problema de pago",
  "billing.state.suspended": "En pausa",
  "billing.state.ended": "Terminado",
  "billing.state.none": "Sin plan",
  "billing.trialEnds": "Tu prueba gratuita termina el {date}. Después cobraremos {amount} a tu tarjeta, salvo que canceles antes.",
  "billing.renews": "Se renueva el {date}.",
  "billing.cancels": "Tu plan termina el {date}. Conservas todo hasta entonces.",
  "billing.grantedByStaff": "Este plan fue concedido a tu cuenta por el equipo de Nemo.",
  "billing.manage": "Gestionar datos de pago y facturas",
  "billing.change": "Cambiar de plan",
  "billing.opening": "Abriendo…",
  "billing.usage": "Este mes",
  "billing.usageConversations": "Conversaciones",
  "billing.usageAgents": "Agentes",
  "billing.usageTeam": "Compañeros de equipo",
  "billing.usedOf": "{used} de {limit} usadas",
  "billing.usedNoLimit": "{used} usadas, sin límite",
  "billing.nearly": "Te acercas al límite. Mejora tu plan para que los visitantes puedan seguir conversando.",
  "billing.full": "Llegaste al límite. Los visitantes ven que el asistente está muy ocupado hasta el próximo periodo, o hasta que mejores tu plan.",
  "billing.plans": "Planes",
  "billing.monthly": "Mensual",
  "billing.yearly": "Anual",
  "billing.perMonth": "{price} / mes",
  "billing.billedYearly": "{total} facturados al año",
  "billing.billedMonthly": "Facturado cada mes",
  "billing.agent1": "1 agente",
  "billing.agentN": "{n} agentes",
  "billing.startTrial": "Empieza tu prueba gratuita de {days} días",
  "billing.subscribe": "Suscribirme",
  "billing.yourPlan": "Tu plan",
  "billing.chosen": "Elegido para ti",
  "billing.trialNote": "Hace falta una tarjeta para empezar. La cobramos cuando termina la prueba, salvo que canceles, y te avisamos 3, 2 y 1 días antes y el mismo día. No hay reembolsos: cada caso se revisa de forma individual.",
  "billing.noTrialNote": "Se cobra hoy. No hay reembolsos: cada caso se revisa de forma individual.",
  "billing.feature.analytics": "Analítica de agentes",
  "billing.feature.remove_branding": "Tu propia marca (sin el logo de Nemo)",
  "billing.feature.advanced_widget": "Personalización avanzada del widget",
  "billing.feature.lead_generation": "Módulo de generación de leads",
  "billing.feature.cloud_integrations": "Integraciones en la nube (Google Cloud, AWS)",
  "billing.feature.bring_your_llm": "Trae tu propio modelo de IA",
  "billing.feature.automation_connections": "Conexiones con plataformas de automatización",
  "billing.feature.dedicated_support": "Soporte dedicado",
  "billing.invoices": "Facturas",
  "billing.noInvoices": "Aún no hay facturas.",
  "billing.invoiceView": "Ver",
  "billing.inv.paid": "Pagada",
  "billing.inv.open": "Pendiente",
  "billing.inv.draft": "Borrador",
  "billing.inv.void": "Anulada",
  "billing.inv.uncollectible": "No cobrada",
  "billing.processing": "¡Gracias! Estamos confirmando tu pago con Stripe. Tarda unos segundos y esta página se actualiza sola.",
  "billing.confirmed": "Tu plan está activo. ¡Gracias!",
  "billing.checkoutCancelled": "Se canceló el pago. No se cobró nada.",
  "billing.held": "{n} de tus agentes están en pausa porque tu plan incluye menos agentes. No se borró nada.",
  "billing.banner.grace": "No pudimos cobrar tu último pago. Todo sigue funcionando hasta el {date}. Por favor actualiza tu tarjeta.",
  "billing.banner.suspended": "Tu pago no se realizó, así que tus agentes publicados están en pausa. Actualiza tu tarjeta y vuelven de inmediato.",
  "billing.banner.ended": "Tu suscripción terminó y tus agentes publicados están en pausa. Tus datos se conservan. Elige un plan para reactivarlos.",
  "billing.banner.trial": "Tu prueba gratuita termina el {date}. Cobraremos tu tarjeta entonces, salvo que canceles antes.",
  "billing.banner.cta": "Abrir facturación",
  "embed.title": "Añádelo a tu sitio web",
  "embed.helpPublished": "Copia esta línea y pégala en tu sitio web. Solo lo haces una vez.",
  "embed.helpDraft": "Primero publica y después pega esto en tu sitio web. No cargará hasta que el agente esté publicado.",
  "embed.code": "Código para incrustar",
  "embed.copy": "Copiar código",
  "embed.copied": "Copiado",
  "embed.never": "Esta línea no cambia cuando editas o vuelves a publicar el agente. El aspecto, los textos y el conocimiento se cargan desde Nemo cada vez.",
  "embed.where": "¿Dónde la pego?",
  "embed.wordpress": "WordPress",
  "embed.wordpress.how": "Instala el plugin gratuito \"WPCode\", abre Code Snippets > Header & Footer, pega la línea en el cuadro Footer y guarda.",
  "embed.wix": "Wix",
  "embed.wix.how": "Configuración > Código personalizado > Añadir código. Pega la línea, elige \"Final del body\" y \"Todas las páginas\", y publica tu sitio. (No uses el elemento \"Insertar HTML\" para la burbuja: no puede salir de su marco.) Para mostrar el chat dentro de una página, activa \"Enlace público del chat\" más abajo y usa Añadir > Código incrustado > Insertar un sitio con ese enlace, tras añadir la dirección de tu Wix a Dominios permitidos.",
  "embed.squarespace": "Squarespace",
  "embed.squarespace.how": "Configuración > Herramientas de desarrollador > Inyección de código. Pega la línea en el cuadro Footer y guarda.",
  "embed.shopify": "Shopify",
  "embed.shopify.how": "Tienda online > Temas > Editar código > theme.liquid. Pega la línea justo antes de </body> y guarda.",
  "embed.html": "HTML normal",
  "embed.html.how": "Pega la línea justo antes de la etiqueta de cierre </body> de cada página.",
  "embed.gtm": "Google Tag Manager",
  "embed.gtm.how": "Etiquetas > Nueva > HTML personalizado. Pega la línea, actívala en Todas las páginas y publica el contenedor.",
  "embed.inline": "¿Quieres el chat dentro de una página en lugar de una burbuja?",
  "embed.inline.how": "Pon esta caja en la página donde debe aparecer el chat. Si la página tiene la caja, el chat se muestra dentro; si no, es una burbuja flotante.",
  "embed.inline.label": "Caja para el chat",
  "link.title": "Enlace público del chat",
  "link.toggle": "Cualquiera con el enlace puede chatear con este agente",
  "link.help": "Le da al agente su propia página web que puedes compartir, poner detrás de un código QR o mostrar dentro de un creador de sitios que solo acepta la dirección de una página (por ejemplo \"Insertar un sitio\" de Wix). Desactivado por defecto. Los visitantes chatean sin iniciar sesión, así que cuenta para tus conversaciones como cualquier otro chat. No añade ningún dominio a tus dominios permitidos.",
  "link.publish": "Publica el agente para que el enlace funcione. Los cambios de este interruptor se aplican al publicar.",
  "link.label": "Enlace público del chat",
  "link.copy": "Copiar enlace",
  "link.frames": "Para mostrar esta página dentro de otro sitio web, añade ese sitio a Dominios permitidos. Los demás sitios no pueden incrustarla.",
  "blocked.title": "Sitios web que intentaron cargar este agente",
  "blocked.help": "Estos sitios tienen el script de Nemo pero no están en tus dominios permitidos, así que el chat no apareció. No se añade nada hasta que pulses Permitir: la lista de dominios permitidos evita que otros gasten tu crédito de IA.",
  "blocked.tried": "{origin} intentó cargar este agente. ¿Permitirlo?",
  "blocked.triedNote": "{origin} intentó cargar \"{agent}\". ¿Permitirlo?",
  "blocked.adds": "Añade {entries}.",
  "mm.title": "Acción necesaria: la búsqueda de tu agente necesita una versión nueva",
  "mm.body": "El servicio que impulsa la búsqueda de este agente se va a retirar. Hemos preparado en segundo plano una versión nueva de su conocimiento; los visitantes aún no se ven afectados. Pruébala y, si cambiaste tus documentos hace poco, vuelve a ejecutar el entrenamiento antes de cambiar.",
  "mm.preparing": "Seguimos preparando la versión nueva ({done} de {total} fragmentos). Te avisaremos cuando esté lista para probar.",
  "mm.preparingNoCount": "Seguimos preparando la versión nueva. Te avisaremos cuando esté lista para probar.",
  "mm.days": "Si nadie la cambia, la cambiaremos automáticamente en {n} días.",
  "mm.daysOne": "Si nadie la cambia, la cambiaremos automáticamente mañana.",
  "mm.daysToday": "Si nadie la cambia, la cambiaremos automáticamente hoy.",
  "mm.pastDue": "La fecha del cambio ya ha pasado.",
  "mm.testTitle": "Probar con la versión nueva",
  "mm.testHint": "Hazle al agente una pregunta que deba saber. La respuesta viene de la versión nueva; no se guarda ni se cambia nada.",
  "mm.testLabel": "Tu pregunta de prueba",
  "mm.testButton": "Probar con la versión nueva",
  "mm.testing": "Probando…",
  "mm.answer": "Respuesta de la versión nueva:",
  "mm.tested": "Ya has probado la versión nueva.",
  "mm.switch": "Cambiar ahora",
  "mm.switching": "Cambiando…",
  "mm.reviewed": "Ya lo he revisado",
  "mm.notReady": "Podrás probar y cambiar cuando la versión nueva esté lista.",
  "mm.ackedTitle": "Cambio de búsqueda revisado",
  "mm.ackedBody": "Lo marcaste como revisado. Aún puedes probar y cambiar cuando quieras.",
  "mm.switchedTitle": "Cambiado a la versión nueva",
  "mm.switchedBody": "Tu agente ya responde con la versión nueva. Vuelve a ejecutar el entrenamiento si cambiaste documentos hace poco.",
  "mm.undo": "Puedes deshacerlo hasta el {date}.",
  "mm.open": "Abrir el agente",
  "src.label": "Recordar de dónde llegan los visitantes",
  "src.help": "Durante la visita, el chat recuerda la página y el enlace de campaña desde los que llegó el visitante (por ejemplo un enlace de Google Ads) y lo envía con sus mensajes, para que veas qué canal trajo cada conversación o contacto. Se guarda la dirección completa de la página, con sus parámetros (los valores que parecen contraseñas o tokens se ocultan), junto con la página de la que venía. En el navegador del visitante solo se conserva en la pestaña abierta. Activado por defecto.",
  "src.notice": "Añade esta frase a tu aviso de privacidad: «Nuestro chat recuerda, durante tu visita, la página y el enlace de campaña desde los que llegaste, para saber qué canal nuestro te trajo.»",
  "mm.current": "Modelo de búsqueda actual",
  "mm.new": "Modelo de búsqueda nuevo",
  "mm.retires": "El actual se retira el",
  "mm.switchesOn": "Cambio automático el",
  "mm.notice": "Leer el aviso oficial del proveedor",
  "mm.newTab": "(se abre en una pestaña nueva)",
  "mm.noNotice": "No hay un aviso del proveedor disponible para este cambio.",
  "blocked.allow": "Permitir",
  "blocked.dismiss": "Descartar",
  "blocked.allowLabel": "Permitir {origin}",
  "blocked.dismissLabel": "Descartar {origin}",
  "blocked.allowed": "Permitido: {entries}. El chat ya funciona allí.",
  "blocked.dismissed": "Descartado.",
  "blocked.none": "Ningún sitio ha intentado cargar este agente sin estar permitido.",
  "blocked.suggest": "¿{domain} es la web de tu empresa? Puedes permitirla.",
  "blocked.suggestAllow": "Permitir {domain}",
  "blocked.ask": "Pide a un propietario o gestor que lo permita.",
  "blocked.failed": "No se pudo hacer. Inténtalo de nuevo.",
  "checksite.title": "Revisar mi sitio web",
  "check.help": "Escribe la dirección de una página donde pegaste la línea. Leemos el texto de la página para ver si está bien configurada. Nunca ejecutamos nada de tu página.",
  "check.label": "Dirección de la página",
  "check.button": "Revisar",
  "check.busy": "Revisando…",
  "check.good": "Todo parece correcto.",
  "check.fix": "Corrige lo marcado arriba y vuelve a revisar.",
  "check.p.invalid_url": "Eso no parece la dirección de una página web. Prueba con algo como www.ejemplo.com.",
  "check.p.blocked": "Solo podemos leer sitios web públicos. Esa dirección no está permitida.",
  "check.p.timeout": "La página tardó demasiado en responder.",
  "check.p.unreachable": "No pudimos llegar a esa página. Revisa la dirección y que el sitio esté en línea.",
  "check.p.http_error": "La página respondió con un error ({status}). Ábrela en tu navegador para comprobar que existe y es pública.",
  "check.p.not_html": "Esa dirección no es una página web.",
  "check.p.too_large": "Esa página es demasiado grande para revisarla.",
  "check.p.rate_limited": "Espera un momento antes de volver a revisar.",
  "check.agent.published": "El agente está publicado.",
  "check.agent.not_published": "El agente aún no está publicado. Publícalo más abajo y vuelve a revisar.",
  "check.script.ok": "La línea de Nemo está en la página y apunta a este agente.",
  "check.script.ok_with_api": "La línea de Nemo está en la página. Es la versión antigua con dirección de API; sigue funcionando, pero puedes cambiarla por la línea más corta de arriba.",
  "check.script.missing": "No encontramos la línea de Nemo en esta página. Pégala como se muestra arriba. Si la añades con Google Tag Manager o con un creador de sitios que añade código después de cargar la página, esta revisión no puede verla.",
  "check.script.wrong_agent": "La página tiene una línea de Nemo de otro agente ({found}). Cámbiala por la línea de arriba.",
  "check.script.wrong_address": "La línea de Nemo está, pero su dirección ({found}) no es la actual. Cámbiala por la línea de arriba; la dirección debe ser {expected}.",
  "check.script.twice": "La línea aparece más de una vez. Deja solo una.",
  "check.script.commented_out": "La línea de Nemo está en la página pero dentro de un comentario, así que no se ejecuta. Quita las marcas de comentario que la rodean.",
  "check.script.consent_blocked": "La línea de Nemo está en la página, pero tu herramienta de cookies la retiene hasta que los visitantes acepten las cookies. Hasta entonces nadie verá el chat. En tu herramienta de cookies marca la línea como necesaria (o permítela), o acepta que solo la verán quienes den su permiso.",
  "check.csp.script_blocked": "La configuración de seguridad de tu sitio (Content-Security-Policy) no permite el script de Nemo. Pide a quien gestiona el sitio que añada {host} a script-src.",
  "check.csp.connect_blocked": "La configuración de seguridad de tu sitio (Content-Security-Policy) no deja que el chat hable con Nemo. Pide a quien gestiona el sitio que añada {host} a connect-src.",
  "check.csp.present_ok": "Tu sitio tiene configuración de seguridad (Content-Security-Policy) y permite el chat.",
  "check.allowed.allowed": "{site} está en la lista de permitidos.",
  "check.allowed.not_allowed": "{site} no está en la lista de permitidos, así que el chat no aparecerá allí.",
  "check.allowed.add": "Añadir {entries} a los dominios permitidos",
  "check.allowed.added": "Añadido. Publica para que esté activo.",
  "drive.title": "Google Drive",
  "drive.help": "Elige archivos de tu Google Drive para que el agente aprenda de ellos. Nemo solo puede ver los archivos que elijas.",
  "drive.privacy": "Lo que conectes se guarda y se usa para responder a cualquiera que hable con este agente, así que no conectes archivos confidenciales. Nemo solo abre los archivos que elijas y puedes desconectar cuando quieras.",
  "drive.connect": "Conectar Google Drive",
  "drive.connectAgain": "Conectar de nuevo",
  "drive.opening": "Abriendo…",
  "drive.status.active": "Conectado",
  "drive.status.needsReconnect": "Requiere atención",
  "drive.connectedAs": "Cuenta de Google: {email}",
  "drive.needsReconnect": "Se quitó o caducó el acceso a Google Drive. El agente conserva lo que ya leyó, pero ya no se actualiza. Conecta de nuevo para continuar.",
  "drive.keepSynced": "Mantener estos archivos actualizados cada día",
  "drive.sheets": "En Hojas de cálculo de Google solo se lee la primera hoja.",
  "drive.choose": "Elegir archivos de Google Drive",
  "drive.tooMany": "Elegiste {n} archivos. Nemo acepta hasta {max} a la vez. Elige menos y vuelve a elegir para añadir el resto.",
  "drive.progress": "Importados {done} de {total} archivos…",
  "drive.progress.some": "{done} de {total} archivos están listos. Abre la lista para ver por qué los demás no.",
  "drive.progress.all": "Los {total} archivos están listos.",
  "dfold.title": "Carpeta de Google Drive (compartida con Nemo)",
  "dfold.help": "Para una carpeta con muchos documentos: compártela con Nemo y cada archivo de dentro, también los nuevos, se lee cada día. Nemo solo lee esta carpeta y las carpetas que contiene.",
  "dfold.link": "Enlace de tu carpeta de Google Drive",
  "dfold.continue": "Continuar",
  "dfold.working": "Un momento…",
  "dfold.pendingTitle": "Carpeta pendiente de verificar",
  "dfold.step1": "1. En Google Drive, comparte la carpeta como Lector con esta dirección:",
  "dfold.step2": "2. Dentro de esa carpeta, crea un documento de Google vacío (o cualquier archivo) con exactamente este nombre:",
  "dfold.step2why": "Así se demuestra que la carpeta es tuya; solo puede hacerlo quien puede añadir archivos a ella. Puedes borrarlo después.",
  "dfold.step3": "3. Pulsa Verificar.",
  "dfold.copyAddress": "Copiar dirección",
  "dfold.copyName": "Copiar nombre",
  "dfold.copied": "Copiado",
  "dfold.verify": "Verificar",
  "dfold.cancel": "Cancelar",
  "dfold.verified": "Carpeta verificada. Ahora úsala como fuente de este agente.",
  "dfold.status.active": "Verificada",
  "dfold.use": "Usar esta carpeta como fuente",
  "dfold.added": "Carpeta añadida. Leyéndola ahora.",
  "dfold.stop": "Dejar de usar esta carpeta",
  "dfold.stopHint": "Para que Nemo deje de leerla del todo, deja también de compartir la carpeta en Google Drive. Lo que el agente ya leyó se queda hasta que quites la fuente.",
  "dfold.workspace": "Si tu organización no te deja compartir con direcciones externas, pide a tu administrador de Google Workspace que permita esta dirección, o elige archivos con el botón de arriba.",
  "dfold.sources": "Carpetas de Google Drive en uso",
  "dfold.privacy": "Tú decides qué compartir. Nemo solo lee la carpeta que verificaste; deja de compartirla en Google Drive para cortar el acceso. Lo que se lee se guarda y se usa para responder a cualquiera que hable con este agente, así que no compartas archivos confidenciales.",
  "dfold.err.not_enabled": "Compartir una carpeta aún no está activado.",
  "dfold.err.held": "Compartir una carpeta está en pausa para tu cuenta. Contacta con soporte.",
  "dfold.err.bad_link": "Eso no parece un enlace de carpeta de Google Drive.",
  "dfold.err.cannot_open": "No pudimos abrir esta carpeta. Comprueba que la compartiste con la dirección indicada y que el archivo con el nombre de arriba está dentro, e inténtalo de nuevo.",
  "dfold.err.expired": "El código caducó. Cancela y pega el enlace de nuevo para obtener uno nuevo.",
  "dfold.err.not_pending": "Esta carpeta ya está verificada o se quitó.",
  "dfold.err.too_many_attempts": "Demasiados intentos. Espera una hora e inténtalo de nuevo.",
  "dfold.err.too_many_pending": "Tienes muchas carpetas en espera. Termina o cancela algunas primero.",
  "dfold.err.not_found": "No encontramos esa carpeta.",
  "dfold.err.folder_not_ready": "Esta carpeta no está verificada. Verifícala primero.",
  "dfold.err.already_added": "Esta carpeta ya es una fuente de este agente.",
  "dfold.err.forbidden": "Solo los propietarios y gerentes de la cuenta pueden hacer esto.",
  "dfold.err.limit_reached": "El agente alcanzó su límite de documentos de entrenamiento.",
  "dfold.err.other": "Algo salió mal. Inténtalo de nuevo.",
  "drive.added": "Se añadieron {n} archivo(s). Leyéndolos ahora.",
  "drive.sources": "Fuentes de Google Drive",
  "drive.files": "Archivos de esta fuente",
  "drive.showFiles": "Ver archivos",
  "drive.hideFiles": "Ocultar archivos",
  "drive.syncNow": "Actualizar ahora",
  "drive.remove": "Quitar",
  "drive.removeSource": "Quitar {name}",
  "drive.lastSync": "Última actualización: {when}.",
  "drive.neverSynced": "Aún sin actualizar.",
  "drive.importedOnce": "Leído una vez; no se mantiene actualizado.",
  "drive.source.pending": "En espera",
  "drive.source.indexing": "Leyendo…",
  "drive.source.indexed": "Listo",
  "drive.source.error": "Requiere atención",
  "drive.source.unsupported": "No disponible",
  "drive.file.indexed": "Listo",
  "drive.file.pending": "En espera",
  "drive.file.not_found": "No encontrado",
  "drive.file.no_access": "Sin acceso",
  "drive.file.unsupported": "No compatible",
  "drive.file.failed": "No se pudo leer",
  "drive.disconnect": "Desconectar Google Drive",
  "drive.disconnect.title": "¿Qué debe pasar con lo que el agente ya leyó de Google Drive?",
  "drive.disconnect.keep": "Conservarlo (deja de actualizarse)",
  "drive.disconnect.remove": "Quitarlo del agente",
  "drive.disconnect.confirm": "Desconectar",
  "drive.cancel": "Cancelar",
  "drive.disconnected.kept": "Desconectado. Lo que el agente leyó se conserva pero ya no se actualiza.",
  "drive.disconnected.removed": "Desconectado. Se quitó lo que el agente leyó de Google Drive.",
  "drive.result.connected": "Google Drive está conectado. Ahora elige los archivos.",
  "drive.result.denied": "No diste acceso, así que no se conectó nada.",
  "drive.result.missing_scope": "No se dio el permiso para abrir los archivos que elijas. Conecta de nuevo y deja esa casilla marcada.",
  "drive.result.bad_state": "Esa conexión tardó demasiado o ya se usó. Empieza de nuevo.",
  "drive.result.provider_failed": "Google no respondió. Inténtalo de nuevo en unos minutos.",
  "drive.result.held": "Google Drive está en pausa para tu cuenta. Contacta con soporte.",
  "drive.result.not_enabled": "Google Drive aún no está activado.",
  "drive.err.not_connected": "Conecta Google Drive primero.",
  "drive.err.needs_reconnect": "La conexión dejó de funcionar. Conecta de nuevo.",
  "drive.err.held": "Google Drive está en pausa para tu cuenta. Contacta con soporte.",
  "drive.err.limit_reached": "El agente alcanzó su límite de documentos de entrenamiento.",
  "drive.err.too_soon": "Se actualizó hace un momento. Inténtalo de nuevo en unos minutos.",
  "drive.err.forbidden": "Solo los propietarios y gerentes de la cuenta pueden hacer esto.",
  "drive.err.not_enabled": "Google Drive aún no está activado.",
  "drive.err.rate_limited": "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
  "drive.err.other": "Algo salió mal. Inténtalo de nuevo.",
  "check.viewport.ok": "La página está preparada para móviles (se encontró la configuración viewport).",
  "check.viewport.missing": "Esta página no tiene configuración viewport para móvil, así que en teléfonos la página se ve diminuta y alejada, y el chat también se ve diminuto y no puede usar toda la pantalla. Pide a quien gestiona el sitio que añada <meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"> dentro de <head>.",
  "check.viewport.no_width": "La configuración viewport de esta página ({found}) no indica width=device-width, así que en teléfonos la página y el chat pueden verse diminutos. Pide a quien gestiona el sitio que use content=\"width=device-width, initial-scale=1\".",
  "embed.viewport": "Tu página también necesita esta línea dentro de su <head> para que los teléfonos la muestren (y al chat) al tamaño correcto: <meta name=\"viewport\" content=\"width=device-width, initial-scale=1\">. La mayoría de creadores de sitios web ya la añaden; usa «Revisar mi sitio web» más abajo para comprobarlo.",
  "check.display.box": "La página tiene una caja (id {id}): el chat aparecerá dentro de ella.",
  "check.display.bubble": "La página no tiene caja para el chat: aparecerá como una burbuja flotante.",
  "check.display.bubble_fallback": "Este agente está configurado para mostrarse dentro de una página, pero esta página no tiene una caja con id {id}, así que se muestra como burbuja flotante.",
  "check.tag_manager.gtm_present": "Esta página usa Google Tag Manager. Si añadiste la línea ahí, esta revisión no puede verla. Mira la página en una ventana privada para estar seguro.",
  "topics.title": "Temas",
  "topics.examples": "Añadir un tema de ejemplo:",
  "topics.placeholder": "Cambia la parte entre [corchetes] de la respuesta por tus propios datos.",
  "topics.name": "Nombre del tema",
  "topics.name.help": "Solo lo ves tú.",
  "topics.triggers": "Frases que lo activan",
  "topics.addTrigger": "Añadir frase",
  "topics.trigger.placeholder": "p. ej. horario de atención",
  "topics.answer": "Respuesta exacta",
  "topics.answer.help": "Se envía tal cual. Que sea corta; lo más largo va en el conocimiento. Escribe las direcciones web completas (https://…).",
  "topics.on": "El tema está activo",
  "topics.untitled": "Tema nuevo {n}",
  "topics.shadowed": "“{trigger}” ya la cubre el tema “{by}” de arriba, que responde primero.",
  "topics.oversize": "Este tema es más grande de lo recomendado. Sigue funcionando; acórtalo cuando lo edites.",
  "topics.incomplete": "Aún sin guardar: un tema necesita nombre, una frase y una respuesta.",
  "topics.up": "Subir {title}",
  "topics.down": "Bajar {title}",
  "topics.moved": "{title} pasó a la posición {n}",
  "topics.remove": "Quitar tema",
  "topics.add": "Añadir tema",
  "topics.test": "Probar los temas",
  "topics.test.help": "Escribe lo que podría escribir un visitante. Usa la misma coincidencia que el chat; no se envía nada a la IA.",
  "topics.test.hit": "El tema “{title}” respondería:",
  "topics.test.miss": "Ningún tema coincide. La IA respondería con tu conocimiento.",
  "ocr.button": "Leer páginas escaneadas",
  "ocr.buttonFor": "Leer las páginas escaneadas de {name}",
  "ocr.checking": "Revisando…",
  "ocr.group": "Lectura de páginas escaneadas de {name}",
  "ocr.summary": "{n} páginas escaneadas por leer, unos {usd} de uso de IA, unos {min} min. No empieza nada hasta que pulses Empezar.",
  "ocr.start": "Empezar a leer",
  "ocr.cancel": "Cancelar",
  "ocr.stop": "Detener lectura",
  "ocr.changed": "El precio cambió. Mira la nueva estimación y empieza de nuevo.",
  "ocr.failed": "No se pudo hacer ahora mismo. Inténtalo en un momento.",
  "ocr.r.ocr_off": "La lectura de páginas escaneadas está desactivada por ahora.",
  "ocr.r.no_reader": "La lectura de páginas escaneadas no está disponible en este servidor.",
  "ocr.r.not_pdf": "Solo se pueden leer así los documentos PDF.",
  "ocr.r.nothing_to_read": "Este documento no tiene páginas escaneadas que leer.",
  "ocr.r.too_many_pages": "Este documento tiene más páginas escaneadas de las que se pueden leer de una vez. Divídelo en partes.",
  "ocr.r.day_cap": "Se alcanzó el límite diario de lectura de páginas escaneadas. Inténtalo mañana.",
  "ocr.r.month_cap": "Se alcanzó el límite mensual de lectura de páginas escaneadas.",
  "ocr.r.running": "Este documento ya se está leyendo.",
  "ocr.r.unreadable": "No se pudo abrir este PDF (puede estar dañado o protegido).",
  "docs.title": "Documentos de entrenamiento",
  "docs.help": "Hasta {max} archivos por agente, de {mb} MB cada uno: PDF, Word (.docx), Excel (.xlsx), CSV y texto. Los PDF escaneados (fotos de páginas) todavía no se pueden leer. Archivos .doc y .xls antiguos y presentaciones: guárdalos primero como .docx, .xlsx o PDF.",
  "docs.upload": "Subir archivos",
  "docs.upload.label": "Subir documentos de entrenamiento",
  "docs.stop": "Detener",
  "docs.uploading": "Subiendo {done} de {total}…",
  "docs.removing": "Quitando {done} de {total}…",
  "docs.retrying": "Reintentando {done} de {total}…",
  "docs.progress": "Documentos entrenados hasta ahora",
  "docs.summary": "{total} documentos: {ready} listos, {working} en curso, {attention} necesitan atención.",
  "docs.search": "Buscar por nombre",
  "docs.filter": "Mostrar",
  "docs.f.all": "Todos",
  "docs.f.ready": "Listos",
  "docs.f.working": "En curso",
  "docs.f.attention": "Necesitan atención",
  "docs.f.unsupported": "No compatibles",
  "docs.retryAll": "Reintentar todos los que necesitan atención ({n})",
  "docs.none": "Ningún documento coincide.",
  "docs.selectPage": "Seleccionar todos en esta página",
  "docs.selectAll": "Seleccionar los {n} que coinciden",
  "docs.bulk": "Acciones para los documentos seleccionados",
  "docs.selected": "{n} seleccionados",
  "docs.removeSel": "Quitar seleccionados",
  "docs.clear": "Borrar selección",
  "docs.confirm": "¿Quitar {n} documentos? No se puede deshacer.",
  "docs.confirmYes": "Sí, quitar",
  "docs.keep": "Conservarlos",
  "docs.select": "Seleccionar {name}",
  "docs.remove": "Quitar {name}",
  "docs.removeOne": "Quitar",
  "docs.retry": "Reintentar {name}",
  "docs.retryOne": "Reintentar",
  "docs.passages": "{n} fragmentos",
  "docs.s.pending": "En espera",
  "docs.s.indexing": "Entrenando…",
  "docs.s.indexed": "Listo",
  "docs.s.error": "Necesita atención",
  "docs.s.unsupported": "No compatible",
  "docs.pages": "Páginas de documentos",
  "docs.showing": "Mostrando {from}–{to} de {n}",
  "docs.prev": "Anterior",
  "docs.next": "Siguiente",
  "docs.page": "Página {p} de {pages}",
  "docs.tooBig": "{name}: los archivos pueden pesar hasta {mb} MB.",
  "docs.badType": "{name}: este tipo de archivo no es compatible. Usa PDF, Word (.docx), Excel (.xlsx), CSV o texto.",
  "docs.failedOne": "{name}: no se pudo añadir.",
  "docs.limit": "Llegaste al límite de {max} documentos para este agente. El resto no se añadió.",
  "docs.skipped": "{n} archivos no se añadieron. El primero: {first}",
  "docs.someFailed": "{n} no se pudieron hacer. Inténtalo de nuevo.",
  "example.group": "Ejemplos",
  "example.label": "¿Necesitas un punto de partida?",
  "example.general": "Insertar ejemplo: general",
  "example.service": "Insertar ejemplo: empresa de servicios",
  "example.replace": "¿Reemplazar lo que escribiste?",
  "example.yes": "Reemplazar",
  "example.no": "Conservar el mío",
  "ready.title": "¿Listo para publicar?",
  "ready.count": "{n} de {total} hechos",
  "ready.advice": "Solo un consejo: puedes publicar cuando quieras.",
  "ready.s.done": "Hecho",
  "ready.s.tip": "Se puede mejorar",
  "ready.s.todo": "Aún sin hacer",
  "ready.go": "Ir al ajuste",
  "ready.try": "Abrir la vista previa",
  "ready.looksgood": "Se ve bien",
  "ready.warnings": "Cosas a revisar",
  "ready.device": "Las marcas \"Se ve bien\" se guardan solo en este dispositivo.",
  "fix.title": "¿El chat no aparece en mi sitio web?",
  "fix.intro": "Los motivos más habituales y qué hacer:",
  "fix.domain.q": "El sitio no está en la lista de permitidos.",
  "fix.domain.a": "Un sitio con www y sin www cuenta como dos. Usa Revisar mi sitio web arriba, pulsa Permitir y luego Publicar.",
  "fix.place.q": "La línea está en el sitio equivocado o sin guardar.",
  "fix.place.a": "Pégala una sola vez, al final del código de la página en todas las páginas, y guarda y publica el sitio (Wix y Squarespace solo muestran los cambios tras publicar).",
  "fix.published.q": "El agente no está publicado.",
  "fix.published.a": "Pulsa Publicar en Nemo. Hasta entonces los visitantes no ven nada.",
  "fix.plan.q": "Se agotó el límite de tu plan.",
  "fix.plan.a": "El chat se pausa hasta el siguiente periodo o hasta que mejores el plan. Verás un aviso en Nemo.",
  "fix.blocker.q": "El bloqueador de anuncios o el modo privado de un visitante lo oculta.",
  "fix.blocker.a": "Algunos visitantes no verán el chat. Comparte el enlace del chat público como alternativa.",
  "fix.csp.q": "La configuración de seguridad de tu sitio lo bloquea.",
  "fix.csp.a": "Revisar mi sitio web te dice qué dirección permitir; envíasela a quien gestiona el sitio.",
  "fix.banner.q": "Un aviso de cookies tapa la burbuja.",
  "fix.banner.a": "Sube la burbuja: añade data-bottom-offset a la línea, por ejemplo 80 (píxeles), como abajo.",
  "fix.doctor": "Para ver exactamente el motivo, abre tu sitio con #nemo-doctor al final de la dirección (por ejemplo www.misitio.com/#nemo-doctor) y abre la consola del navegador (F12). Nemo escribe una línea clara por cada hallazgo. Para subir la burbuja sobre un aviso:",
  "fix.lift.label": "Línea de ejemplo con la burbuja subida",
  "check.builder.wix": "Parece un sitio Wix. Para la burbuja flotante usa Configuración > Código personalizado (Final del body, Todas las páginas). Para un chat dentro de una página usa el enlace público con Añadir > Código incrustado > Insertar un sitio.",
  "check.builder.squarespace": "Parece un sitio Squarespace. Usa Configuración > Herramientas de desarrollador > Inyección de código > Footer. Para un chat dentro de una página, usa el enlace público en un bloque Insertar.",
  "check.builder.shopify": "Parece una tienda Shopify. Usa Tienda online > Temas > Editar código > theme.liquid, justo antes de </body>.",
  "check.builder.wordpress": "Parece un sitio WordPress. Usa el plugin WPCode (Code Snippets > Header & Footer > Footer).",
  "check.builder.webflow": "Parece un sitio Webflow. Usa Project Settings > Custom Code > Footer Code y publica el sitio.",
  "check.status.ok": "Correcto",
  "check.status.fail": "Hay que corregirlo",
  "check.status.warn": "Revisa esto",
  "check.status.info": "Nota",
  "legal.title": "Avisos legales",
  "legal.help": "Se muestran después del saludo, en este orden. Cada uno es un texto corto (hasta 500 caracteres) o un enlace a una página, por ejemplo tu política de privacidad. Hasta {max}.",
  "legal.none": "Sin avisos legales.",
  "legal.n": "Aviso {n}",
  "legal.type": "Tipo del aviso {n}",
  "legal.text": "Texto",
  "legal.link": "Enlace",
  "legal.placeholderText": "Escribe el texto legal que verán los visitantes…",
  "legal.up": "Subir el aviso {n}",
  "legal.down": "Bajar el aviso {n}",
  "legal.remove": "Quitar el aviso {n}",
  "legal.add": "Añadir un aviso",
  "avatar.upload": "Subir una imagen",
  "avatar.replace": "Cambiar imagen",
  "avatar.adjust": "Ajustar",
  "avatar.remove": "Quitar imagen",
  "avatar.drop": "Suelta una imagen aquí, o elige una desde tu ordenador o móvil.",
  "avatar.rules": "JPG, PNG o WebP, hasta 5 MB. Funciona mejor una cara o un logo que llene el marco.",
  "avatar.fileInput": "Elegir un archivo de imagen",
  "avatar.alt": "Imagen de {name}",
  "avatar.thisAgent": "este agente",
  "avatar.frame": "Marco de la imagen. Arrastra la imagen para moverla, o usa las flechas. Más y menos cambian el tamaño.",
  "avatar.help": "Arrastra la imagen, usa las flechas (con Mayús, pasos más grandes), la rueda del ratón o dos dedos para hacer zoom.",
  "avatar.zoom": "Zoom",
  "avatar.preview": "Así se verá en el chat:",
  "avatar.save": "Usar esta imagen",
  "avatar.saving": "Guardando…",
  "avatar.saved": "Imagen guardada. Se verá en el chat cuando publiques.",
  "avatar.removed": "Imagen quitada. El chat muestra la predeterminada.",
  "avatar.or": "O usa una imagen de internet",
  "avatar.urlLabel": "Dirección de la imagen",
  "avatar.urlHelp": "Opcional. Pega la dirección de una imagen cuadrada.",
  "avatar.err.too_large": "Ese archivo es demasiado grande. Elige una imagen de menos de 5 MB.",
  "avatar.err.too_many_pixels": "Esa imagen es demasiado grande. Elige una menor de unos 6000 por 5000 píxeles.",
  "avatar.err.unsupported_type": "Solo podemos usar imágenes JPG, PNG o WebP (no SVG ni GIF).",
  "avatar.err.animated": "No se aceptan imágenes animadas. Elige una imagen fija.",
  "avatar.err.too_small": "Esa imagen es demasiado pequeña. Elige una de al menos 64 píxeles por lado.",
  "avatar.err.corrupt": "No pudimos leer esa imagen. Prueba con otra.",
  "avatar.err.no_file": "Primero elige una imagen.",
  "avatar.err.rate_limited": "Demasiadas imágenes en poco tiempo. Espera un minuto e inténtalo de nuevo.",
  "avatar.err.no_source": "No hay imagen que ajustar. Sube una primero.",
  "avatar.err.busy": "Se están procesando muchas imágenes ahora. Inténtalo de nuevo en unos segundos.",
  "avatar.err.generic": "No se pudo guardar la imagen. Inténtalo de nuevo.",
};

export const dictionaries = { en, es } as const;

export type Translate = (key: Key, vars?: Record<string, string | number>) => string;

export function translator(lang: Lang): Translate {
  return (key, vars) => {
    const raw = (lang === "es" ? es[key] : undefined) ?? en[key];
    return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? "")) : raw;
  };
}

/** The language to start in: what the person chose before, else their browser's, else English. */
export function initialLang(): Lang {
  try {
    const saved = localStorage.getItem("nemo.settings.lang");
    if (saved === "en" || saved === "es") return saved;
  } catch {
    /* storage blocked */
  }
  return typeof navigator !== "undefined" && /^es\b/i.test(navigator.language) ? "es" : "en";
}

export function rememberLang(lang: Lang): void {
  try {
    localStorage.setItem("nemo.settings.lang", lang);
  } catch {
    /* ignore */
  }
}

const I18nContext = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: Translate }>({ lang: "en", setLang: () => {}, t: translator("en") });

export function I18nProvider({ lang, setLang, children }: { lang: Lang; setLang: (l: Lang) => void; children: ReactNode }) {
  const value = useMemo(() => ({ lang, setLang, t: translator(lang) }), [lang, setLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
